import Box from "@mui/material/Box";
import {
    Autocomplete,
    Button,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    Collapse,
    FormControlLabel,
    Popover,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from "@mui/material";
import { useState, useEffect, useCallback, useRef } from "react";
import { ApiResponse } from "../../models/ApiResponse";
import api from "../../services/ApiService";
import { KeyValues, ProductFilterOptions } from "../../models/Items/FilterOptions";
import { ItemListRequest, SelectedFilters } from "../../models/Items/ItemListRequest";
import { ExpandMore, ExpandLess, MoreVert, Close } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from '../../store';
import {
  updateDashboardViewSelectedFilterOptions,
  updateDashboardViewSelectedFilterValues,
  updateInactiveItemsViewSelectedFilterOptions,
  updateInactiveItemsViewSelectedFilterValues,
  updateProductListViewSelectedFilterOptions,
  updateProductListViewSelectedFilterValues,
  updateQuoteListViewSelectedFilterOptions,
  updateQuoteListViewSelectedFilterValues,
} from '../../reducers/filtersReducer';
import { Permissions, PermissionsFriendlyNames } from "../../models/Enum";
import { FIELD_NAMES, ITEM_STATUS_OPTIONS, MULTI_SELECT_FIELD_NAMES, NO_SL_INTRO_DATE, NO_TAGS_OPTION, NO_VENDOR, PRODUCT_TYPE_DROPDOWN_OPTIONS, PROJECT_MANAGER_ASSIGNMENT, QUOTE_APPROVAL_TYPE_DROPDOWN_OPTIONS,
  VIEW_ATTACHMENTS, VIEW_INACTIVE_PRODUCTS, VIEW_NOTIFICATION_LOGS, VIEW_PRODUCT_ASSIGNMENT, VIEW_STATUS_REPORTS, VIEW_TOOLINGS
 } from "./EditItem/Constants";
import { PrjStageWithStatus } from "../../models/PrjStage";
import moment from "moment";
import { DATE_FORMAT } from "../../utils/constants";
import { Tag } from "../../models/Tag";
import { updateActiveProjectListSelectedFilterOptions, updateActiveProjectListSelectedFilterValues } from "../../reducers/projectDashboardReducer";
import { updateToolingListViewSelectedFilterOptions, updateToolingListViewSelectedFilterValues } from "../../reducers/toolingsReducer";
import { updateAttachmentViewSelectedFilterOptions, updateAttachmentViewSelectedFilterValues } from "../../reducers/attachmentFiltersReducer";
import { updateProductAssignmentsViewSelectedFilterOptions, updateProductAssignmentsViewSelectedFilterValues } from "../../reducers/productAssignmentReducer";
import { toast } from "react-toastify";
import { updateItemListRequest } from "../../reducers/itemsReducer";
import { updateQuoteListRequest } from "../../reducers/quotesReducer";
import { updateAttachmentListRequest } from "../../reducers/attachmentsReducer";
import { initialItemListRequest } from "./ProductList";
import { initialQuoteListRequest } from "./Quotes/QuoteList";
import { initialAttachmentListRequest } from "../Report/ViewAttachment/AttachmentList";
import { User } from "../../models/User";
import { sortByKey, sortCVLFieldOptions, formatSlIntroDateFilterOption, getInputTitle } from "../../utils/helpers";
import { updateStatusReportsViewSelectedFilterOptions, updateStatusReportsViewSelectedFilterValues } from "../../reducers/statusReportsReducer";
import { ItemFunction } from "../../models/ItemFunction";
import AutocompleteWithModal from "../../components/AutocompleteWithModal";
import { initialNotificationLogRequest, updateIsUserCleared, updateNotificationLogRequest, updateNotificationLogViewSelectedFilterOptions, updateNotificationLogViewSelectedFilterValues } from "../../reducers/notificationLogsReducer";
import { updateInactiveItemListRequest } from "../../reducers/inactiveItemsReducer";
import SessionService from "../../services/SessionService";
import {
  buildDynamicFiltersRequest,
  buildDynamicFiltersUrl,
  DYNAMIC_FILTER_STATIC_FIELDS,
  enrichFilterValuesWithMultiSelectSelections,
  extractFieldFilterOptions,
  DynamicFilterFieldResponse,
  FilterValuesState,
  getFilterContextCacheKey,
  getEmptyFilterOptionsForField,
  hasActiveFilterSelections,
} from "../../utils/dynamicFilters";

type FiltersProps = {
  onApplyFilter: (filterValues: SelectedFilters) => void;
  filtersExpanded: boolean;
  handleExpandClick: () => void;
  viewMode: string;
};

const Filters = (props: FiltersProps) => {

  const {
    filtersExpanded,
    viewMode,
    onApplyFilter,
    handleExpandClick
  } = props;

  // these fields stores the reference id and not the actual value e.g. Stage, Category
  const REFERENCE_FIELDS = [
    "stage",
    "status",
    "marketingDesigner",
    "category",
    "function",
    "brandCollection",
    "primaryMaterial",
    "secondaryMaterial",
    "developmentType",
    "quoteApprovalType",
    "vendor",
    "assignment",
    "assignmentId",
    "attachmentTypeId",
    "seriesName",
    "userId",
    "itemStatus"
  ];

  const multiSelectFields = [
    MULTI_SELECT_FIELD_NAMES.stage,
    MULTI_SELECT_FIELD_NAMES.status,
    MULTI_SELECT_FIELD_NAMES.marketingDesigner,
    MULTI_SELECT_FIELD_NAMES.function,
    MULTI_SELECT_FIELD_NAMES.slIntroDate,
    MULTI_SELECT_FIELD_NAMES.pdFamily,
    MULTI_SELECT_FIELD_NAMES.seriesName,
    MULTI_SELECT_FIELD_NAMES.vendor,
    MULTI_SELECT_FIELD_NAMES.developmentType,
    MULTI_SELECT_FIELD_NAMES.tags
  ];

  const fieldHasNoId = (fieldName) => [MULTI_SELECT_FIELD_NAMES.slIntroDate, MULTI_SELECT_FIELD_NAMES.pdFamily,].includes(fieldName) 

  const initialSelectedFilters: SelectedFilters = {};
  const [filterOptions, setFilterOptions] = useState<ProductFilterOptions>({});
  const [productListStatusDisabled, setProductListStatusDisabled] = useState<boolean>(false);
  const [inactiveItemsStatusDisabled, setInactiveItemsStatusDisabled] = useState<boolean>(false);
  const [notificationLogsStatusDisabled, setNotificationLogsStatusDisabled] = useState<boolean>(false);
  const [dashboardStatusDisabled, setDashboardStatusDisabled] = useState<boolean>(false);
  const [quoteListStatusDisabled, setQuoteListStatusDisabled] = useState<boolean>(false);
  const [attachmentListStatusDisabled, setAttachmentListStatusDisabled] = useState<boolean>(false);
  const [productAssignmentListStatusDisabled, setProductAssignmentListStatusDisabled] = useState<boolean>(false);
  const [projectOverviewDashboardStatusDisabled, setProjectOverviewDashboardStatusDisabled] = useState<boolean>(false);
  const [functionDropdownDisabled, setFunctionDropdownDisabled] = useState<boolean>(false);
  const [allFunctionOptions, setAllFunctionOptions] = useState<ItemFunction[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [filterSetName, setFilterSetName] = useState<string>();
  const [deleteModal, setDeleteModal]  = useState<{isOpen: boolean; filterName?: string}>({isOpen: false, filterName: ""});
  const dispatch = useAppDispatch();
  const [isResetCalled, setIsResetCalled] = useState<boolean>(false);
  const [hasShownItemStatusToast, setHasShownItemStatusToast] = useState(false);
  
  const productListFilterOptionsSelected = useAppSelector((state) => state.filters.productListView.selectedFilterOptions);
  const productListFilterValuesSelected = useAppSelector((state) => state.filters.productListView.selectedFilterValues);

  const quoteListFilterOptionsSelected = useAppSelector((state) => state.filters.quoteListView.selectedFilterOptions);
  const quoteListFilterValuesSelected = useAppSelector((state) => state.filters.quoteListView.selectedFilterValues);
  
  const dashboardFilterOptionsSelected = useAppSelector((state) => state.filters.dashboardView.selectedFilterOptions);
  const dashboardFilterValuesSelected = useAppSelector((state) => state.filters.dashboardView.selectedFilterValues);
  const dashboardExecutiveReviewTagId = useAppSelector((state) => state.filters.dashboardView.reviewRequiredTagId);

  const projectOverviewDashboardFilterOptionsSelected = useAppSelector((state) => state.projectDashboard.activeProjectListFilters.selectedFilterOptions);
  const projectOverviewDashboardFilterValuesSelected = useAppSelector((state) => state.projectDashboard.activeProjectListFilters.selectedFilterValues);

  const toolingListFilterOptionsSelected = useAppSelector((state) => state.toolings.toolingListFilter.selectedFilterOptions);
  const toolingListFilterValuesSelected = useAppSelector((state) => state.toolings.toolingListFilter.selectedFilterValues);

  const attachmentListFilterOptionsSelected = useAppSelector((state) => state.attachmentFilters.attachmentListView.selectedFilterOptions);
  const attachmentListFilterValuesSelected = useAppSelector((state) => state.attachmentFilters.attachmentListView.selectedFilterValues);

  const productAssignmentsListFilterOptionsSelected = useAppSelector((state) => state.productAssignments.productAssignmentsListView.selectedFilterOptions);
  const productAssignmentsListFilterValuesSelected = useAppSelector((state) => state.productAssignments.productAssignmentsListView.selectedFilterValues);

  const statusReportsListFilterOptionsSelected = useAppSelector((state) => state.statusReports.statusReportsListView.selectedFilterOptions);
  const statusReportsListFilterValuesSelected = useAppSelector((state) => state.statusReports.statusReportsListView.selectedFilterValues);

  const notificationLogsFilterOptionsSelected = useAppSelector((state) => state.notificationLogs.notificationLogFilter.selectedFilterOptions);
  const notificationLogsListFilterValuesSelected = useAppSelector((state) => state.notificationLogs.notificationLogFilter.selectedFilterValues);

  const inactiveItemsFilterOptionsSelected = useAppSelector((state) => state.filters.inactiveItemsView.selectedFilterOptions);
  const inactiveItemsFilterValuesSelected = useAppSelector((state) => state.filters.inactiveItemsView.selectedFilterValues);

  const isUserCleared = useAppSelector((state) => state.notificationLogs.isUserCleared);
  const sessionService = SessionService.getInstance();

  const isEffectExecuted = useRef(false);
  const dynamicOptionsCacheRef = useRef<Map<string, ProductFilterOptions | DynamicFilterFieldResponse>>(new Map());
  const dynamicFiltersAbortControllerRef = useRef<AbortController | null>(null);
  const dynamicFiltersDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filterContextVersionRef = useRef(0);
  const initialFilterOptionsRef = useRef<ProductFilterOptions>({});
  const [dynamicFilterLoadingFields, setDynamicFilterLoadingFields] = useState<Record<string, boolean>>({});
  const filtersKeyMapping = {
    brandCollection: "Brand Collection",
    [MULTI_SELECT_FIELD_NAMES.stage]: "Stages",
    [MULTI_SELECT_FIELD_NAMES.status]: "Statuses",
    [MULTI_SELECT_FIELD_NAMES.marketingDesigner]: "Designers",
    [MULTI_SELECT_FIELD_NAMES.seriesName]: "Series Names",
    [MULTI_SELECT_FIELD_NAMES.pdFamily]: "PD Families",
    category: "Category",
    [MULTI_SELECT_FIELD_NAMES.function]: "Functions",
    baseCode: "Base Code",
    pdCode: "PD BaseCode",
    stockCode: "Stock Code",
    primaryMaterial: "Primary Material",
    secondaryMaterial: "Secondary Material",
    [MULTI_SELECT_FIELD_NAMES.slIntroDate]: "SL Intro Dates",
    [MULTI_SELECT_FIELD_NAMES.tags]: "Tags",
    [MULTI_SELECT_FIELD_NAMES.vendor]: "Vendors",
    [MULTI_SELECT_FIELD_NAMES.developmentType]: "Development Type",
    attachmentTypeId: "Attachment Type",
    quoteDate: "Quote Date",
    quoteApprovalType: "Quote Approval Type",
    assignment: "Assignment",
    assignmentId: "Assignment",
    userId: "User",
    projectCode: "Product Id",
    itemStatus: "Item Status",
    hideReleasedToInriver: "Hide Released To Inriver",
    hideArchivedDevelopment: "Hide Archived Development"
  }
  
  useEffect(() => {
    getAllFilterOptions();
    if(
      (viewMode === PermissionsFriendlyNames[Permissions.ViewProducts] && !productListFilterValuesSelected?.developmentTypes) 
      || (viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS] && !inactiveItemsFilterValuesSelected?.developmentTypes)
        || (viewMode === PermissionsFriendlyNames[VIEW_STATUS_REPORTS] && !statusReportsListFilterValuesSelected?.developmentTypes)
        || (viewMode === PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS] && !notificationLogsListFilterValuesSelected?.developmentTypes)) {
      setSelectedProductTypeFilter();
    }
  }, []);

  useEffect(() => {
    if (isEffectExecuted.current) return;

    if (Object.keys(filterOptions).length > 0) {
      isEffectExecuted.current = true; // Ensure this runs only once
      if (dashboardExecutiveReviewTagId) {
        setSelectedTagsFilter();
      }

      if(viewMode === PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) {
        const findProjectManagerAssignment = filterOptions?.assignments?.find((assignment) => assignment.name === PROJECT_MANAGER_ASSIGNMENT);
        
        dispatch(updateActiveProjectListSelectedFilterValues({
          ...projectOverviewDashboardFilterValuesSelected,
          assignment: projectOverviewDashboardFilterValuesSelected?.assignment || findProjectManagerAssignment?.id,
        }));
        dispatch(updateActiveProjectListSelectedFilterOptions({
          ...projectOverviewDashboardFilterOptionsSelected,
          assignment: projectOverviewDashboardFilterOptionsSelected?.assignment || findProjectManagerAssignment,
        }));
      }
    }
  }, [filterOptions, dashboardExecutiveReviewTagId]);

  const prepareStatusDropDownOptionsBasedOnStage = useCallback((stageId: number[]) => {
    if (!stageId?.length) {
      return;
    }

    // Use functional updates so this does not re-bind whenever filterOptions changes
    // (otherwise dynamic Status API results get overwritten by stageStatus mapping).
    setFilterOptions((prev) => {
      if (!prev?.stageStatus?.length) {
        return prev;
      }

      const newStagesStatuses = [];
      const filteredStages = prev.stageStatus.filter((stage) => stageId.includes(stage.id));
      filteredStages.forEach((stage) => {
        stage.statuses.forEach((status) => {
          if (!newStagesStatuses.some((s) => s.id === status.id)) {
            newStagesStatuses.push({
              id: status.id,
              name: status.description,
              description: status.description,
            });
          }
        });
      });

      newStagesStatuses.sort(sortByKey("description"));
      const currentStatuses = prev.projectStatuses || [];
      if (JSON.stringify(currentStatuses) === JSON.stringify(newStagesStatuses)) {
        return prev;
      }

      return {
        ...prev,
        projectStatuses: newStagesStatuses,
      };
    });
  }, []);

  const prepareFunctionDropDownOptionsBasedOnCategory = useCallback((categoryId) => {
    if (!categoryId) {
      return;
    }

    // Use functional updates so dynamic Function API results are not overwritten
    // when filterOptions changes after a dynamic-filters response.
    setFilterOptions((prev) => {
      if (!prev?.productcategories?.length) {
        return prev;
      }

      const functionDropDownOptions = allFunctionOptions
        ?.filter((f) => f?.category === categoryId)
        .map((f) => ({
          ...f,
          name: f?.description,
          description: f?.description,
        }))
        .sort(sortByKey("description")) || [];

      const currentFunctions = prev.productFunctions || [];
      if (JSON.stringify(currentFunctions) === JSON.stringify(functionDropDownOptions)) {
        return prev;
      }

      return {
        ...prev,
        productFunctions: functionDropDownOptions,
      };
    });
  }, [allFunctionOptions]);

  const setStatusDropDownBasedOnStageValue = useCallback(() => {
    const viewModeToFilterMap = {
      [PermissionsFriendlyNames[Permissions.ExecutiveDashboard]]: dashboardFilterValuesSelected,
      [PermissionsFriendlyNames[Permissions.ViewProducts]]: productListFilterValuesSelected,
      [PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]]: inactiveItemsFilterValuesSelected,
      [PermissionsFriendlyNames[Permissions.ViewQuotes]]: quoteListFilterValuesSelected,
      [PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]]: projectOverviewDashboardFilterValuesSelected,
      [PermissionsFriendlyNames[VIEW_ATTACHMENTS]]: attachmentListFilterValuesSelected,
      [PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT]]: productAssignmentsListFilterValuesSelected,
      [PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]]: notificationLogsListFilterValuesSelected,
    };

    const selectedFilters = viewModeToFilterMap[viewMode] || {};
    // When filters are not at default/reset, Status options come from dynamic-filters API only.
    // Do not rebind from stageStatus — that restores the full stage status list after selection.
    const shouldBindStatusOptionsFromStage = !hasActiveFilterSelections(selectedFilters);

    if (viewMode === PermissionsFriendlyNames[Permissions.ExecutiveDashboard] && dashboardFilterValuesSelected?.stages?.length > 0) {
      if (shouldBindStatusOptionsFromStage) {
        prepareStatusDropDownOptionsBasedOnStage(dashboardFilterValuesSelected.stages);
      }
      setDashboardStatusDisabled(false);
    }
    else if(viewMode === PermissionsFriendlyNames[Permissions.ExecutiveDashboard] && !(dashboardFilterValuesSelected?.stages?.length > 0)) {
      setDashboardStatusDisabled(true);
    }

    if (viewMode === PermissionsFriendlyNames[Permissions.ViewProducts] && productListFilterValuesSelected?.stages?.length > 0) {
      if (shouldBindStatusOptionsFromStage) {
        prepareStatusDropDownOptionsBasedOnStage(productListFilterValuesSelected.stages);
      }
      setProductListStatusDisabled(false);
    }
    else if(viewMode === PermissionsFriendlyNames[Permissions.ViewProducts] && !(productListFilterValuesSelected?.stages?.length > 0)) {
      setProductListStatusDisabled(true);
    }

    if (viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS] && inactiveItemsFilterValuesSelected?.stages?.length > 0) {
      if (shouldBindStatusOptionsFromStage) {
        prepareStatusDropDownOptionsBasedOnStage(inactiveItemsFilterValuesSelected.stages);
      }
      setInactiveItemsStatusDisabled(false);
    }
    else if(viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS] && !(inactiveItemsFilterValuesSelected?.stages?.length > 0)) {
      setInactiveItemsStatusDisabled(true);
    }

    if (viewMode === PermissionsFriendlyNames[Permissions.ViewQuotes] && quoteListFilterValuesSelected?.stages?.length > 0) {
      if (shouldBindStatusOptionsFromStage) {
        prepareStatusDropDownOptionsBasedOnStage(quoteListFilterValuesSelected.stages);
      }
      setQuoteListStatusDisabled(false);
    }
    else if(viewMode === PermissionsFriendlyNames[Permissions.ViewQuotes] && !(quoteListFilterValuesSelected?.stages?.length > 0)) {
      setQuoteListStatusDisabled(true);
    }

    if (viewMode === PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard] && projectOverviewDashboardFilterValuesSelected?.stages?.length > 0) {
      if (shouldBindStatusOptionsFromStage) {
        prepareStatusDropDownOptionsBasedOnStage(projectOverviewDashboardFilterValuesSelected.stages);
      }
      setProjectOverviewDashboardStatusDisabled(false);
    }
    else if(viewMode === PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard] && !(projectOverviewDashboardFilterValuesSelected?.stages?.length > 0)) {
      setProjectOverviewDashboardStatusDisabled(true);
    }

    if (viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS] && attachmentListFilterValuesSelected?.stages?.length > 0) {
      if (shouldBindStatusOptionsFromStage) {
        prepareStatusDropDownOptionsBasedOnStage(attachmentListFilterValuesSelected.stages);
      }
      setAttachmentListStatusDisabled(false);
    }
    else if(viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS] && !(attachmentListFilterValuesSelected?.stages?.length > 0)) {
      setAttachmentListStatusDisabled(true);
    }

    if (viewMode === PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT] && productAssignmentsListFilterValuesSelected?.stages?.length > 0) {
      if (shouldBindStatusOptionsFromStage) {
        prepareStatusDropDownOptionsBasedOnStage(productAssignmentsListFilterValuesSelected.stages);
      }
      setProductAssignmentListStatusDisabled(false);
    }
    else if(viewMode === PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT] && !(productAssignmentsListFilterValuesSelected?.stages?.length > 0)) {
      setProductAssignmentListStatusDisabled(true);
    }
    if (viewMode === PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS] && notificationLogsListFilterValuesSelected?.stages?.length > 0) {
      if (shouldBindStatusOptionsFromStage) {
        prepareStatusDropDownOptionsBasedOnStage(notificationLogsListFilterValuesSelected.stages);
      }
      setNotificationLogsStatusDisabled(false);
    }
    else if(viewMode === PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS] && !(notificationLogsListFilterValuesSelected?.stages?.length > 0)) {
      setNotificationLogsStatusDisabled(true);
    }

  }, [
    viewMode,
    dashboardFilterValuesSelected,
    productListFilterValuesSelected,
    inactiveItemsFilterValuesSelected,
    quoteListFilterValuesSelected,
    toolingListFilterValuesSelected,
    projectOverviewDashboardFilterValuesSelected,
    attachmentListFilterValuesSelected,
    productAssignmentsListFilterValuesSelected,
    prepareStatusDropDownOptionsBasedOnStage,
    PermissionsFriendlyNames,
    Permissions,
    notificationLogsListFilterValuesSelected
  ]);

  const setFunctionDropDownBasedOnCategoryValue = useCallback(() => {
    const viewModeToFilterMap = {
      [PermissionsFriendlyNames[Permissions.ExecutiveDashboard]]: dashboardFilterValuesSelected,
      [PermissionsFriendlyNames[Permissions.ViewProducts]]: productListFilterValuesSelected,
      [PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]]: inactiveItemsFilterValuesSelected,
      [PermissionsFriendlyNames[Permissions.ViewQuotes]]: quoteListFilterValuesSelected,
      [PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]]: projectOverviewDashboardFilterValuesSelected,
      [PermissionsFriendlyNames[VIEW_ATTACHMENTS]]: attachmentListFilterValuesSelected,
      [PermissionsFriendlyNames[VIEW_STATUS_REPORTS]]: statusReportsListFilterValuesSelected,
      [PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT]]: productAssignmentsListFilterValuesSelected,
      [PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]]: notificationLogsListFilterValuesSelected,
    };

    const selectedFilters = viewModeToFilterMap[viewMode] || {};
    // When filters are not at default/reset, Function options come from dynamic-filters API only.
    const shouldBindFunctionOptionsFromCategory = !hasActiveFilterSelections(selectedFilters);

    if (selectedFilters?.category) {
      if (shouldBindFunctionOptionsFromCategory) {
        prepareFunctionDropDownOptionsBasedOnCategory(selectedFilters.category);
      }
      setFunctionDropdownDisabled(false);
    } else {
      setFunctionDropdownDisabled(true);
    }
  }, [
    viewMode,
    dashboardFilterValuesSelected,
    productListFilterValuesSelected,
    inactiveItemsFilterValuesSelected,
    quoteListFilterValuesSelected,
    projectOverviewDashboardFilterValuesSelected,
    attachmentListFilterValuesSelected,
    statusReportsListFilterValuesSelected,
    prepareFunctionDropDownOptionsBasedOnCategory,
    productAssignmentsListFilterValuesSelected,
    PermissionsFriendlyNames,
    Permissions,
    notificationLogsListFilterValuesSelected
  ]);

  useEffect(() => {
    setStatusDropDownBasedOnStageValue();
  }, [
    dashboardFilterValuesSelected,
    productListFilterValuesSelected,
    inactiveItemsFilterValuesSelected,
    quoteListFilterValuesSelected,
    projectOverviewDashboardFilterValuesSelected,
    attachmentListFilterValuesSelected,
    productAssignmentsListFilterValuesSelected,
    setStatusDropDownBasedOnStageValue,
    notificationLogsListFilterValuesSelected
  ]);

  useEffect(() => {
    setFunctionDropDownBasedOnCategoryValue();
  }, [
    dashboardFilterValuesSelected,
    productListFilterValuesSelected,
    inactiveItemsFilterValuesSelected,
    quoteListFilterValuesSelected,
    projectOverviewDashboardFilterValuesSelected,
    attachmentListFilterValuesSelected,
    statusReportsListFilterValuesSelected,
    setFunctionDropDownBasedOnCategoryValue,
    productAssignmentsListFilterValuesSelected,
  ]);

  const setSelectedTagsFilter = () => {
    if (filterOptions?.projectTags?.length > 0) {
      const setReviewTagFromOptions = filterOptions.projectTags.find((pt) => pt.id === dashboardExecutiveReviewTagId);
      dispatch(updateDashboardViewSelectedFilterValues({
        ...dashboardFilterValuesSelected,
        tags: [setReviewTagFromOptions.id],
      }));

      dispatch(updateDashboardViewSelectedFilterOptions({
        ...dashboardFilterOptionsSelected,
        tags: [setReviewTagFromOptions],
      }));
    }
  };

  const setSelectedProductTypeFilter = () => {
    if(viewMode === PermissionsFriendlyNames[Permissions.ViewProducts]) {
      dispatch(updateProductListViewSelectedFilterValues({
        ...productListFilterValuesSelected,
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0].id],
      }));
  
      dispatch(updateProductListViewSelectedFilterOptions({
        ...productListFilterOptionsSelected,
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0]],
      }));
    } else if(viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]) {
      dispatch(updateInactiveItemsViewSelectedFilterValues({
        ...inactiveItemsFilterValuesSelected,
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0].id],
      }));
  
      dispatch(updateInactiveItemsViewSelectedFilterOptions({
        ...inactiveItemsFilterOptionsSelected,
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0]],
      }));
    } else if(viewMode === PermissionsFriendlyNames[VIEW_STATUS_REPORTS]) {
      dispatch(updateStatusReportsViewSelectedFilterValues({
        ...statusReportsListFilterValuesSelected,
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0].id],
      }));
  
      dispatch(updateStatusReportsViewSelectedFilterOptions({
        ...statusReportsListFilterOptionsSelected,
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0]],
      }));
    } else if(viewMode === PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]) {
      dispatch(updateNotificationLogViewSelectedFilterValues({
        ...notificationLogsListFilterValuesSelected,
        userId: !isUserCleared && !notificationLogsListFilterValuesSelected?.userId 
          ? sessionService.getCurrentUser().id : undefined,
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0].id],
      }));
  
      dispatch(updateNotificationLogViewSelectedFilterOptions({
        ...notificationLogsFilterOptionsSelected,
        userId: !isUserCleared && !notificationLogsListFilterValuesSelected?.userId 
          ? {id: sessionService.getCurrentUser().id, name: sessionService.getCurrentUser().name} : undefined,
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0]],
      }));
    }
  };

  const applyFilterOptionsPostProcessing = useCallback((
    data: ProductFilterOptions,
    stageStatus?: PrjStageWithStatus[]
  ): ProductFilterOptions => {
    const processed = { ...data };

    if (stageStatus) {
      processed.stageStatus = stageStatus;
    }

    sortCVLFieldOptions(processed, ["productIntroDatess"]);

    const productIntroDates = [...(processed?.productIntroDatess || [])];
    if (!productIntroDates.includes(NO_SL_INTRO_DATE)) {
      productIntroDates.push(NO_SL_INTRO_DATE);
    }
    processed.productIntroDatess = productIntroDates as string[];

    processed.productSuppliers = [...(processed?.productSuppliers || []), { id: 0, name: NO_VENDOR }];
    if (![PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard], VIEW_TOOLINGS, VIEW_PRODUCT_ASSIGNMENT].includes(viewMode)) {
      processed.projectTags = [...(processed?.projectTags || []), NO_TAGS_OPTION];
    }

    return processed;
  }, [viewMode]);

  const getCurrentSelectedFilterValues = useCallback((): FilterValuesState => {
    switch (viewMode) {
      case PermissionsFriendlyNames[Permissions.ViewProducts]:
        return productListFilterValuesSelected;
      case PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]:
        return inactiveItemsFilterValuesSelected;
      case PermissionsFriendlyNames[Permissions.ViewQuotes]:
        return quoteListFilterValuesSelected;
      case VIEW_TOOLINGS:
        return toolingListFilterValuesSelected;
      case PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT]:
        return productAssignmentsListFilterValuesSelected;
      case PermissionsFriendlyNames[VIEW_ATTACHMENTS]:
        return attachmentListFilterValuesSelected;
      case PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]:
        return projectOverviewDashboardFilterValuesSelected;
      case PermissionsFriendlyNames[VIEW_STATUS_REPORTS]:
        return statusReportsListFilterValuesSelected;
      case PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]:
        return notificationLogsListFilterValuesSelected;
      case PermissionsFriendlyNames[Permissions.ExecutiveDashboard]:
        return dashboardFilterValuesSelected;
      default:
        return {};
    }
  }, [
    viewMode,
    productListFilterValuesSelected,
    inactiveItemsFilterValuesSelected,
    quoteListFilterValuesSelected,
    toolingListFilterValuesSelected,
    productAssignmentsListFilterValuesSelected,
    attachmentListFilterValuesSelected,
    projectOverviewDashboardFilterValuesSelected,
    statusReportsListFilterValuesSelected,
    notificationLogsListFilterValuesSelected,
    dashboardFilterValuesSelected,
  ]);

  const getCurrentSelectedFilterOptions = useCallback((): Record<string, unknown> => {
    switch (viewMode) {
      case PermissionsFriendlyNames[Permissions.ViewProducts]:
        return productListFilterOptionsSelected;
      case PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]:
        return inactiveItemsFilterOptionsSelected;
      case PermissionsFriendlyNames[Permissions.ViewQuotes]:
        return quoteListFilterOptionsSelected;
      case VIEW_TOOLINGS:
        return toolingListFilterOptionsSelected;
      case PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT]:
        return productAssignmentsListFilterOptionsSelected;
      case PermissionsFriendlyNames[VIEW_ATTACHMENTS]:
        return attachmentListFilterOptionsSelected;
      case PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]:
        return projectOverviewDashboardFilterOptionsSelected;
      case PermissionsFriendlyNames[VIEW_STATUS_REPORTS]:
        return statusReportsListFilterOptionsSelected;
      case PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]:
        return notificationLogsFilterOptionsSelected;
      case PermissionsFriendlyNames[Permissions.ExecutiveDashboard]:
        return dashboardFilterOptionsSelected;
      default:
        return {};
    }
  }, [
    viewMode,
    productListFilterOptionsSelected,
    inactiveItemsFilterOptionsSelected,
    quoteListFilterOptionsSelected,
    toolingListFilterOptionsSelected,
    productAssignmentsListFilterOptionsSelected,
    attachmentListFilterOptionsSelected,
    projectOverviewDashboardFilterOptionsSelected,
    statusReportsListFilterOptionsSelected,
    notificationLogsFilterOptionsSelected,
    dashboardFilterOptionsSelected,
  ]);

  const getFilterValuesForDynamicRequest = useCallback((): FilterValuesState => {
    return enrichFilterValuesWithMultiSelectSelections(
      getCurrentSelectedFilterValues(),
      getCurrentSelectedFilterOptions()
    );
  }, [getCurrentSelectedFilterValues, getCurrentSelectedFilterOptions]);

  const setDynamicFieldLoading = useCallback((uiFieldName: string, isLoading: boolean) => {
    setDynamicFilterLoadingFields((prev) => {
      if (Boolean(prev[uiFieldName]) === isLoading) {
        return prev;
      }

      return { ...prev, [uiFieldName]: isLoading };
    });
  }, []);

  const isDynamicFilterFieldLoading = useCallback(
    (uiFieldName: string) => Boolean(dynamicFilterLoadingFields[uiFieldName]),
    [dynamicFilterLoadingFields]
  );

  const clearFieldFilterOptions = useCallback((uiFieldName: string) => {
    setFilterOptions((prev) => ({
      ...prev,
      ...getEmptyFilterOptionsForField(uiFieldName),
    }));
  }, []);

  const getDynamicFilterOptionList = useCallback(
    (uiFieldName: string, optionsList: Array<any> | undefined) =>
      isDynamicFilterFieldLoading(uiFieldName) ? [] : (optionsList ?? []),
    [isDynamicFilterFieldLoading]
  );

  const invalidateDynamicOptionsCache = useCallback(() => {
    dynamicOptionsCacheRef.current.clear();
    filterContextVersionRef.current += 1;
    dynamicFiltersAbortControllerRef.current?.abort();
  }, []);

  const mergeDynamicFilterOptions = useCallback((data: ProductFilterOptions | DynamicFilterFieldResponse, uiFieldName?: string) => {
    setFilterOptions((prev) => {
      const fieldUpdates = uiFieldName
        ? extractFieldFilterOptions(uiFieldName, data, viewMode)
        : Array.isArray(data)
          ? {}
          : applyFilterOptionsPostProcessing(data, prev.stageStatus);

      return { ...prev, ...fieldUpdates };
    });

    // Re-bind dependent dropdowns only when the parent field changes.
    // Do not re-run after Status/Function dynamic responses — that overwrites API options.
    if (!uiFieldName || uiFieldName === MULTI_SELECT_FIELD_NAMES.stage) {
      setStatusDropDownBasedOnStageValue();
    }
    if (!uiFieldName || uiFieldName === FIELD_NAMES.category) {
      setFunctionDropDownBasedOnCategoryValue();
    }
  }, [applyFilterOptionsPostProcessing, setStatusDropDownBasedOnStageValue, setFunctionDropDownBasedOnCategoryValue, viewMode]);

  const restoreInitialFilterOptions = useCallback((uiFieldName?: string) => {
    if (!Object.keys(initialFilterOptionsRef.current).length) {
      return;
    }

    mergeDynamicFilterOptions(initialFilterOptionsRef.current, uiFieldName);
  }, [mergeDynamicFilterOptions]);

  const refreshDynamicFilterOptions = useCallback((uiFieldName: string) => {
    if (DYNAMIC_FILTER_STATIC_FIELDS.has(uiFieldName)) {
      return;
    }

    if (dynamicFiltersDebounceRef.current) {
      clearTimeout(dynamicFiltersDebounceRef.current);
    }

    dynamicFiltersDebounceRef.current = setTimeout(async () => {
      const requestContextVersion = filterContextVersionRef.current;
      const currentFilterValues = getFilterValuesForDynamicRequest();

      try {
        if (!hasActiveFilterSelections(currentFilterValues)) {
          restoreInitialFilterOptions(uiFieldName);
          return;
        }

        const cacheKey = getFilterContextCacheKey(currentFilterValues, viewMode, uiFieldName);
        const cachedOptions = dynamicOptionsCacheRef.current.get(cacheKey);

        if (cachedOptions) {
          mergeDynamicFilterOptions(cachedOptions, uiFieldName);
          return;
        }

        dynamicFiltersAbortControllerRef.current?.abort();
        const abortController = new AbortController();
        dynamicFiltersAbortControllerRef.current = abortController;

        const requestPayload = buildDynamicFiltersRequest(currentFilterValues, viewMode, uiFieldName);
        const response = await api.post<ApiResponse<ProductFilterOptions | DynamicFilterFieldResponse>>(
          buildDynamicFiltersUrl(uiFieldName),
          requestPayload,
          {
            signal: abortController.signal,
            headers: { disableLoader: true },
          }
        );

        if (requestContextVersion !== filterContextVersionRef.current) {
          return;
        }

        if (response?.isSuccess && response.data) {
          dynamicOptionsCacheRef.current.set(cacheKey, response.data);
          mergeDynamicFilterOptions(response.data, uiFieldName);
        } else if (Object.keys(initialFilterOptionsRef.current).length > 0) {
          mergeDynamicFilterOptions(initialFilterOptionsRef.current, uiFieldName);
        }
      } catch (error) {
        if ((error as Error)?.name !== "CanceledError" && (error as Error)?.name !== "AbortError") {
          console.error("Exception from dynamic filter options", error);
          if (Object.keys(initialFilterOptionsRef.current).length > 0) {
            mergeDynamicFilterOptions(initialFilterOptionsRef.current, uiFieldName);
          }
        }
      } finally {
        setDynamicFieldLoading(uiFieldName, false);
      }
    }, 200);
  }, [
    getFilterValuesForDynamicRequest,
    mergeDynamicFilterOptions,
    restoreInitialFilterOptions,
    setDynamicFieldLoading,
    viewMode,
  ]);

  const handleDropdownOpen = useCallback((uiFieldName: string) => {
    if (DYNAMIC_FILTER_STATIC_FIELDS.has(uiFieldName)) {
      return;
    }

    const currentFilterValues = getFilterValuesForDynamicRequest();

    if (!hasActiveFilterSelections(currentFilterValues)) {
      restoreInitialFilterOptions(uiFieldName);
      return;
    }

    const cacheKey = getFilterContextCacheKey(currentFilterValues, viewMode, uiFieldName);
    const cachedOptions = dynamicOptionsCacheRef.current.get(cacheKey);

    if (cachedOptions) {
      mergeDynamicFilterOptions(cachedOptions, uiFieldName);
      return;
    }

    clearFieldFilterOptions(uiFieldName);
    setDynamicFieldLoading(uiFieldName, true);
    refreshDynamicFilterOptions(uiFieldName);
  }, [
    clearFieldFilterOptions,
    getFilterValuesForDynamicRequest,
    mergeDynamicFilterOptions,
    refreshDynamicFilterOptions,
    restoreInitialFilterOptions,
    setDynamicFieldLoading,
    viewMode,
  ]);

  useEffect(() => {
    return () => {
      if (dynamicFiltersDebounceRef.current) {
        clearTimeout(dynamicFiltersDebounceRef.current);
      }
      dynamicFiltersAbortControllerRef.current?.abort();
    };
  }, []);

  const getAllFilterOptions = async () => {
    try {
      const dropDownSources = await api.get<ApiResponse<ProductFilterOptions>>(`/Item/filter-options`);

      if (dropDownSources?.isSuccess) {
        const stageStatusResponse = api
          .get<ApiResponse<PrjStageWithStatus[]>>(`/PickList/project-stage-stauses-list`)
          .then((response) => {
            if (response?.isSuccess) {
              return response;
            }
          })
          .catch((error) => {
            console.error("Exception from stage status list api", error);
          });

        const users = api
        .get<ApiResponse<User[]>>(`/UserRole`)
        .then((response: ApiResponse<User[]>) => {
          if (response.isSuccess && response.data?.length > 0) {
            return response.data.filter((ul) => ul.isActive === true).map(ul => {
              return {
                id: ul.id,
                name: ul.name
              }
            });
          } else {
            throw new Error(response?.message);
          }
        })
        .catch((error) => {
          console.error("Exception from User Role", error);
        });

        const [
          stageStatus,
          usersRes
        ] = await Promise.all([
          stageStatusResponse,
          users
        ]);

        dropDownSources.data.stageStatus = (stageStatus as ApiResponse<PrjStageWithStatus[]>).data;
        dropDownSources.data.users = usersRes as KeyValues[];

        const processedOptions = applyFilterOptionsPostProcessing(
          dropDownSources.data,
          dropDownSources.data.stageStatus
        );
        initialFilterOptionsRef.current = processedOptions;
        setAllFunctionOptions(processedOptions.productFunctions);
        setFilterOptions(processedOptions);
      }
    } catch (error) {
      console.error("Exception from filter options", error);
    }
  };

  const clearFilterSelections = (viewMode, fieldName, selectedFilterValues, selectedFilterOptions) => {
    const isStageField = fieldName === MULTI_SELECT_FIELD_NAMES.stage;
    const isCategoryField = fieldName === FIELD_NAMES.category;

    if(isStageField || isCategoryField) {
      setIsResetCalled(true);
    }

    if (viewMode === PermissionsFriendlyNames[Permissions.ViewProducts]) {
      dispatch(updateProductListViewSelectedFilterValues({
        ...selectedFilterValues,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));
      dispatch(updateProductListViewSelectedFilterOptions({
        ...selectedFilterOptions,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));

      if(fieldName === "itemStatus") {
        setHasShownItemStatusToast(false);
      }
    } else if (viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]) {
      dispatch(updateInactiveItemsViewSelectedFilterValues({
        ...selectedFilterValues,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));
      dispatch(updateInactiveItemsViewSelectedFilterOptions({
        ...selectedFilterOptions,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));
    }
    else if (viewMode === PermissionsFriendlyNames[Permissions.ViewQuotes]) {
      dispatch(updateQuoteListViewSelectedFilterValues({
        ...selectedFilterValues,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));
      dispatch(updateQuoteListViewSelectedFilterOptions({
        ...selectedFilterOptions,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));
    }
    else if (viewMode === VIEW_TOOLINGS) {
      dispatch(updateToolingListViewSelectedFilterValues({
        ...selectedFilterValues,
        [fieldName]: undefined,
      }));
      dispatch(updateToolingListViewSelectedFilterOptions({
        ...selectedFilterOptions,
        [fieldName]: undefined,
      }));
    }
    else if (viewMode === PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT]) {
      dispatch(updateProductAssignmentsViewSelectedFilterValues({
        ...selectedFilterValues,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));
      dispatch(updateProductAssignmentsViewSelectedFilterOptions({
        ...selectedFilterOptions,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));
    }
    else if (viewMode === PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) {
      dispatch(updateActiveProjectListSelectedFilterValues({
        ...selectedFilterValues,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));
      dispatch(updateActiveProjectListSelectedFilterOptions({
        ...selectedFilterOptions,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));
    }
    else if (viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS]) {
      dispatch(updateAttachmentViewSelectedFilterValues({
        ...selectedFilterValues,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));
      dispatch(updateAttachmentViewSelectedFilterOptions({
        ...selectedFilterOptions,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));
    }
    else if (viewMode === PermissionsFriendlyNames[VIEW_STATUS_REPORTS]) {
      dispatch(updateStatusReportsViewSelectedFilterValues({
        ...selectedFilterValues,
        [fieldName]: undefined,
        ...(isCategoryField && { functions: [] }),
      }));
      dispatch(updateStatusReportsViewSelectedFilterOptions({
        ...selectedFilterOptions,
        [fieldName]: undefined,
        ...(isCategoryField && { functions: [] }),
      }));
    }
    else if (viewMode === PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]) {
      if(fieldName === "userId") {
        dispatch(updateIsUserCleared(true));
      }
      dispatch(updateNotificationLogViewSelectedFilterValues({
        ...selectedFilterValues,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));
      dispatch(updateNotificationLogViewSelectedFilterOptions({
        ...selectedFilterOptions,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));

      if(fieldName === "itemStatus") {
        setHasShownItemStatusToast(false);
      }
    }
    else {
      dispatch(updateDashboardViewSelectedFilterValues({
        ...selectedFilterValues,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));
      dispatch(updateDashboardViewSelectedFilterOptions({
        ...selectedFilterOptions,
        [fieldName]: undefined,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] }),
      }));
    }
  };

  const applyFilterSelection = (viewMode, fieldName, selectedValue, selectedItem) => {
    const isCategoryField = fieldName === FIELD_NAMES.category;
    const isStageField = fieldName === MULTI_SELECT_FIELD_NAMES.stage;
    const dispatchActions = {
      [PermissionsFriendlyNames[Permissions.ViewProducts]]: {
        updateValues: updateProductListViewSelectedFilterValues,
        updateOptions: updateProductListViewSelectedFilterOptions,
        values: productListFilterValuesSelected,
        options: productListFilterOptionsSelected,
      },
      [PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]]: {
        updateValues: updateInactiveItemsViewSelectedFilterValues,
        updateOptions: updateInactiveItemsViewSelectedFilterOptions,
        values: inactiveItemsFilterValuesSelected,
        options: inactiveItemsFilterOptionsSelected,
      },
      [PermissionsFriendlyNames[Permissions.ViewQuotes]]: {
        updateValues: updateQuoteListViewSelectedFilterValues,
        updateOptions: updateQuoteListViewSelectedFilterOptions,
        values: quoteListFilterValuesSelected,
        options: quoteListFilterOptionsSelected,
      },
      [VIEW_TOOLINGS]: {
        updateValues: updateToolingListViewSelectedFilterValues,
        updateOptions: updateToolingListViewSelectedFilterOptions,
        values: toolingListFilterValuesSelected,
        options: toolingListFilterOptionsSelected,
      },
      [PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT]]: {
        updateValues: updateProductAssignmentsViewSelectedFilterValues,
        updateOptions: updateProductAssignmentsViewSelectedFilterOptions,
        values: productAssignmentsListFilterValuesSelected,
        options: productAssignmentsListFilterOptionsSelected,
      },
      [PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]]: {
        updateValues: updateActiveProjectListSelectedFilterValues,
        updateOptions: updateActiveProjectListSelectedFilterOptions,
        values: projectOverviewDashboardFilterValuesSelected,
        options: projectOverviewDashboardFilterOptionsSelected,
      },
      [PermissionsFriendlyNames[VIEW_ATTACHMENTS]]: {
        updateValues: updateAttachmentViewSelectedFilterValues,
        updateOptions: updateAttachmentViewSelectedFilterOptions,
        values: attachmentListFilterValuesSelected,
        options: attachmentListFilterOptionsSelected,
      },
      [PermissionsFriendlyNames[VIEW_STATUS_REPORTS]]: {
        updateValues: updateStatusReportsViewSelectedFilterValues,
        updateOptions: updateStatusReportsViewSelectedFilterOptions,
        values: statusReportsListFilterValuesSelected,
        options: statusReportsListFilterOptionsSelected,
      },
      [PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]]: {
        updateValues: updateNotificationLogViewSelectedFilterValues,
        updateOptions: updateNotificationLogViewSelectedFilterOptions,
        values: notificationLogsListFilterValuesSelected,
        options: notificationLogsFilterOptionsSelected,
      },
      default: {
        updateValues: updateDashboardViewSelectedFilterValues,
        updateOptions: updateDashboardViewSelectedFilterOptions,
        values: dashboardFilterValuesSelected,
        options: dashboardFilterOptionsSelected,
      }
    };

    const { updateValues, updateOptions, values, options } = dispatchActions[viewMode] || dispatchActions.default;
    if(JSON.stringify(values[fieldName]) !== JSON.stringify(selectedValue)) {
      if([MULTI_SELECT_FIELD_NAMES.stage, FIELD_NAMES.category].includes(fieldName)) {
        setIsResetCalled(true);
      };
      dispatch(updateValues({
        ...values,
        [fieldName]: selectedValue,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] })
      }));
  
      dispatch(updateOptions({
        ...options,
        [fieldName]: selectedItem,
        ...(isStageField && { statuses: [] }),
        ...(isCategoryField && { functions: [] })
      }));
    }
  };

  const handleFilterChange = (fieldName, selectedItem) => {
    invalidateDynamicOptionsCache();
    if (!selectedItem) {
      const selectedFilterValues = viewMode === PermissionsFriendlyNames[Permissions.ViewProducts]
        ? productListFilterValuesSelected
        : viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]
          ? inactiveItemsFilterValuesSelected
          : viewMode === PermissionsFriendlyNames[Permissions.ViewQuotes]
          ? quoteListFilterValuesSelected 
          : viewMode === VIEW_TOOLINGS
          ? toolingListFilterValuesSelected
          : viewMode === PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT]
          ? productAssignmentsListFilterValuesSelected
          : viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS]
          ? attachmentListFilterValuesSelected
        : viewMode === PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard] ? 
        projectOverviewDashboardFilterValuesSelected 
        : viewMode === PermissionsFriendlyNames[VIEW_STATUS_REPORTS] ? 
        statusReportsListFilterValuesSelected : 
        viewMode === PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]
        ? notificationLogsListFilterValuesSelected : dashboardFilterValuesSelected;
      

      const selectedFilterOptions = viewMode === PermissionsFriendlyNames[Permissions.ViewProducts]
        ? productListFilterOptionsSelected
        : viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]
          ? inactiveItemsFilterOptionsSelected
        : viewMode === PermissionsFriendlyNames[Permissions.ViewQuotes]
          ? quoteListFilterOptionsSelected 
          : viewMode === VIEW_TOOLINGS
          ? toolingListFilterOptionsSelected
          : viewMode === PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT]
          ? productAssignmentsListFilterOptionsSelected
          : viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS]
          ? attachmentListFilterOptionsSelected 
        : viewMode === PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard] ?
        projectOverviewDashboardFilterOptionsSelected : viewMode === PermissionsFriendlyNames[VIEW_STATUS_REPORTS] ? 
        statusReportsListFilterOptionsSelected : 
        viewMode === PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]
        ? notificationLogsFilterOptionsSelected : dashboardFilterOptionsSelected;

      clearFilterSelections(viewMode, fieldName, selectedFilterValues, selectedFilterOptions);

    } else {
      let selectedValue;

      if(typeof selectedItem === "string") {
        selectedValue = selectedItem;
      } else if (Array.isArray(selectedItem) && multiSelectFields.includes(fieldName)) {
        selectedValue = selectedItem.map((item) => fieldHasNoId(fieldName) ? item : item.id);
      } else if (REFERENCE_FIELDS.includes(fieldName)) {
        selectedValue = selectedItem.id;
      } else {
        selectedValue = selectedItem.name;
      }

      // Our toast logic — only for itemStatus field, and only the first time
      if (fieldName === "itemStatus" && !hasShownItemStatusToast && viewMode === PermissionsFriendlyNames[Permissions.ViewProducts]) {
        toast.warn("Checkbox filters (Hide Released To Inriver and Hide Archived Development) will be ignored when an Item Status is selected.");
        setHasShownItemStatusToast(true);
      }

      applyFilterSelection(viewMode, fieldName, selectedValue, selectedItem);
    }
    removeAppliedFilterName();
  };

  const HandleCheckboxChange = (fieldName, checkedItem) => {
    invalidateDynamicOptionsCache();
    if (viewMode === PermissionsFriendlyNames[Permissions.ViewProducts]) {
      dispatch(updateProductListViewSelectedFilterValues({
        ...productListFilterValuesSelected,
        [fieldName]: checkedItem,
      }));
  
      dispatch(updateProductListViewSelectedFilterOptions({
        ...productListFilterOptionsSelected,
        [fieldName]: checkedItem,
      }));
    }
    else if(viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]) {
      dispatch(updateInactiveItemsViewSelectedFilterValues({
        ...inactiveItemsFilterValuesSelected,
        [fieldName]: checkedItem,
      }));
      dispatch(updateInactiveItemsViewSelectedFilterOptions({
        ...inactiveItemsFilterOptionsSelected,
        [fieldName]: checkedItem,
      }));
    }
    else if(viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS]) {
      dispatch(updateAttachmentViewSelectedFilterValues({
        ...attachmentListFilterValuesSelected,
        [fieldName]: checkedItem,
      }));

      dispatch(updateAttachmentViewSelectedFilterOptions({
        ...attachmentListFilterOptionsSelected,
        [fieldName]: checkedItem,
      }));
    }
    removeAppliedFilterName();
  } 

  const handleTagsFilterChange = (fieldName, selectedTags, reason) => {
    invalidateDynamicOptionsCache();

    // store the array with TagIds to send into the api request
    let selectedTagIDs = selectedTags.map(t => t.id);
    if (viewMode === PermissionsFriendlyNames[Permissions.ViewProducts]) {

      // If "Select All" is clicked, either select all or clear all
      if (selectedTags.includes('selectAll')) {
        const selectAllTagIds = filterOptions?.projectTags?.map(t => t.id);

        dispatch(updateProductListViewSelectedFilterValues({
          ...productListFilterValuesSelected,
          [fieldName]: selectAllTagIds,
        }));

        // store the selected tag option in state to display dropdown value selection 
        dispatch(updateProductListViewSelectedFilterOptions({
          ...productListFilterOptionsSelected,
          [fieldName]: filterOptions?.projectTags,
        }));

      } else {

        dispatch(updateProductListViewSelectedFilterValues({
          ...productListFilterValuesSelected,
          [fieldName]: selectedTagIDs,
        }));
    
        // store the selected tag option in state to display dropdown value selection 
        dispatch(updateProductListViewSelectedFilterOptions({
          ...productListFilterOptionsSelected,
          [fieldName]: selectedTags,
        }));
      }
    }
    else if(viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]) {
      if (selectedTags.includes('selectAll')) {
        const selectAllTagIds = filterOptions?.projectTags?.map(t => t.id);

        dispatch(updateInactiveItemsViewSelectedFilterValues({
          ...inactiveItemsFilterValuesSelected,
          [fieldName]: selectAllTagIds,
        }));

        // store the selected tag option in state to display dropdown value selection 
        dispatch(updateInactiveItemsViewSelectedFilterOptions({
          ...inactiveItemsFilterOptionsSelected,
          [fieldName]: filterOptions?.projectTags,
        }));

      } else {

        dispatch(updateInactiveItemsViewSelectedFilterValues({
          ...inactiveItemsFilterValuesSelected,
          [fieldName]: selectedTagIDs,
        }));
    
        // store the selected tag option in state to display dropdown value selection 
        dispatch(updateInactiveItemsViewSelectedFilterOptions({
          ...inactiveItemsFilterOptionsSelected,
          [fieldName]: selectedTags,
        }));
      }
    }
    else if(viewMode === PermissionsFriendlyNames[Permissions.ViewQuotes]) {
      dispatch(updateQuoteListViewSelectedFilterValues({
        ...quoteListFilterValuesSelected,
        [fieldName]: selectedTagIDs,
      }));
  
      // store the selected tag option in state to display dropdown value selection 
      dispatch(updateQuoteListViewSelectedFilterOptions({
        ...quoteListFilterOptionsSelected,
        [fieldName]: selectedTags,
      }));
    }
    else if (viewMode === PermissionsFriendlyNames[Permissions.ExecutiveDashboard]) {
      if (reason === 'removeOption' && selectedTags.findIndex(option => option.id === dashboardExecutiveReviewTagId) === -1) {
        const setReviewTagFromOptions = filterOptions?.projectTags?.find((pt) => pt.id === dashboardExecutiveReviewTagId);
        selectedTags.unshift(setReviewTagFromOptions);
        selectedTagIDs.unshift(dashboardExecutiveReviewTagId);
      }
      dispatch(updateDashboardViewSelectedFilterValues({
        ...dashboardFilterValuesSelected,
        [fieldName]: selectedTagIDs,
      }));

      // store the selected tag option in state to display dropdown value selection 
      dispatch(updateDashboardViewSelectedFilterOptions({
        ...dashboardFilterOptionsSelected,
        [fieldName]: selectedTags,
      }));
    }
    else if (viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS]) {
      dispatch(updateAttachmentViewSelectedFilterValues({
        ...attachmentListFilterValuesSelected,
        [fieldName]: selectedTagIDs,
      }));

      // store the selected tag option in state to display dropdown value selection 
      dispatch(updateAttachmentViewSelectedFilterOptions({
        ...attachmentListFilterOptionsSelected,
        [fieldName]: selectedTags,
      }));
    }
    else if (viewMode === PermissionsFriendlyNames[VIEW_STATUS_REPORTS]) {
      
      // If "Select All" is clicked, either select all or clear all
      if (selectedTags.includes('selectAll')) {
        const selectAllTagIds = filterOptions?.projectTags?.map(t => t.id);

        dispatch(updateStatusReportsViewSelectedFilterValues({
          ...statusReportsListFilterValuesSelected,
          [fieldName]: selectAllTagIds,
        }));

        // store the selected tag option in state to display dropdown value selection 
        dispatch(updateStatusReportsViewSelectedFilterOptions({
          ...statusReportsListFilterOptionsSelected,
          [fieldName]: filterOptions?.projectTags,
        }));

      } else {
        dispatch(updateStatusReportsViewSelectedFilterValues({
          ...statusReportsListFilterValuesSelected,
          [fieldName]: selectedTagIDs,
        }));
        
        // store the selected tag option in state to display dropdown value selection 
        dispatch(updateStatusReportsViewSelectedFilterOptions({
          ...statusReportsListFilterOptionsSelected,
          [fieldName]: selectedTags,
        }));
      }
    } else if (viewMode === PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]) {

      // If "Select All" is clicked, either select all or clear all
      if (selectedTags.includes('selectAll')) {
        const selectAllTagIds = filterOptions?.projectTags?.map(t => t.id);

        dispatch(updateNotificationLogViewSelectedFilterValues({
          ...notificationLogsListFilterValuesSelected,
          [fieldName]: selectAllTagIds,
        }));

        // store the selected tag option in state to display dropdown value selection 
        dispatch(updateNotificationLogViewSelectedFilterOptions({
          ...notificationLogsFilterOptionsSelected,
          [fieldName]: filterOptions?.projectTags,
        }));

      } else {

        dispatch(updateNotificationLogViewSelectedFilterValues({
          ...notificationLogsListFilterValuesSelected,
          [fieldName]: selectedTagIDs,
        }));
    
        // store the selected tag option in state to display dropdown value selection 
        dispatch(updateNotificationLogViewSelectedFilterOptions({
          ...notificationLogsFilterOptionsSelected,
          [fieldName]: selectedTags,
        }));
      }
    }
    removeAppliedFilterName();
  };

  const renderSelectedValue = (fieldName) => {
    if (viewMode === PermissionsFriendlyNames[Permissions.ViewProducts]) {
      if(multiSelectFields.includes(fieldName)) {
        return productListFilterOptionsSelected[fieldName]
        ? productListFilterOptionsSelected[fieldName]
        : []
      }
      else if(fieldName === "projectCode") {
        return productListFilterOptionsSelected[fieldName]
          ? productListFilterOptionsSelected[fieldName]
          : ""
      }
      else if (fieldName === "hideReleasedToInriver" || fieldName === "hideArchivedDevelopment") {
        return productListFilterOptionsSelected[fieldName]
        ? productListFilterOptionsSelected[fieldName]
        : null
      }
      else {
        return productListFilterOptionsSelected[fieldName]
          ? productListFilterOptionsSelected[fieldName]
          : null
      } 
    }
    else if(viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]) {
      if(multiSelectFields.includes(fieldName)) {
        return inactiveItemsFilterOptionsSelected[fieldName]
        ? inactiveItemsFilterOptionsSelected[fieldName]
        : []
      }
      else if(fieldName === "projectCode") {
        return inactiveItemsFilterOptionsSelected[fieldName]
          ? inactiveItemsFilterOptionsSelected[fieldName]
          : ""
      }
      else {
        return inactiveItemsFilterOptionsSelected[fieldName]
          ? inactiveItemsFilterOptionsSelected[fieldName]
          : null
      } 
    }
    else if(viewMode === PermissionsFriendlyNames[Permissions.ViewQuotes]) {
      if(multiSelectFields.includes(fieldName)) {
        return quoteListFilterOptionsSelected[fieldName]
        ? quoteListFilterOptionsSelected[fieldName]
        : []
      }
      else if(fieldName === "projectCode") {
        return quoteListFilterOptionsSelected[fieldName]
          ? quoteListFilterOptionsSelected[fieldName]
          : ""
      }
      else {
        return quoteListFilterOptionsSelected[fieldName]
          ? quoteListFilterOptionsSelected[fieldName]
          : null
      }
    }
    else if(viewMode === VIEW_TOOLINGS) {
      return toolingListFilterOptionsSelected[fieldName]
          ? toolingListFilterOptionsSelected[fieldName]
          : null
    }
    else if(viewMode === PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT]) {
      return productAssignmentsListFilterOptionsSelected[fieldName]
          ? productAssignmentsListFilterOptionsSelected[fieldName]
          : null
    }
    else if(viewMode === PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) {
      return projectOverviewDashboardFilterOptionsSelected[fieldName]
          ? projectOverviewDashboardFilterOptionsSelected[fieldName]
          : null
    }
    else if (viewMode === PermissionsFriendlyNames[Permissions.ExecutiveDashboard]) {
      if(multiSelectFields.includes(fieldName)) {
        return dashboardFilterOptionsSelected[fieldName]
          ? dashboardFilterOptionsSelected[fieldName]
          : []
      }
      else if(fieldName === "projectCode") {
        return dashboardFilterOptionsSelected[fieldName]
          ? dashboardFilterOptionsSelected[fieldName]
          : ""
      }
      else {
        return dashboardFilterOptionsSelected[fieldName]
          ? dashboardFilterOptionsSelected[fieldName]
          : null
      }
    }
    else if (viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS]) {
      if(multiSelectFields.includes(fieldName)) {
        return attachmentListFilterOptionsSelected[fieldName]
        ? attachmentListFilterOptionsSelected[fieldName]
        : []
      }
      else if(fieldName === "projectCode") {
        return attachmentListFilterOptionsSelected[fieldName]
          ? attachmentListFilterOptionsSelected[fieldName]
          : ""
      }
      else if (fieldName === "hideReleasedToInriver" || fieldName === "hideArchivedDevelopment") {
        return attachmentListFilterOptionsSelected[fieldName]
        ? attachmentListFilterOptionsSelected[fieldName]
        : null
      }
      else {
        return attachmentListFilterOptionsSelected[fieldName]
          ? attachmentListFilterOptionsSelected[fieldName]
          : null
      } 
    }
    else if (viewMode === PermissionsFriendlyNames[VIEW_STATUS_REPORTS]) {
      if(multiSelectFields.includes(fieldName)) {
        return statusReportsListFilterOptionsSelected[fieldName]
        ? statusReportsListFilterOptionsSelected[fieldName]
        : []
      }
      else if(fieldName === "projectCode") {
        return statusReportsListFilterOptionsSelected[fieldName]
          ? statusReportsListFilterOptionsSelected[fieldName]
          : ""
      }
      else {
        return statusReportsListFilterOptionsSelected[fieldName]
          ? statusReportsListFilterOptionsSelected[fieldName]
          : null
      } 
    } else if (viewMode === PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]) {
      if(multiSelectFields.includes(fieldName)) {
        return notificationLogsFilterOptionsSelected[fieldName]
        ? notificationLogsFilterOptionsSelected[fieldName]
        : []
      }
      else if(fieldName === "projectCode") {
        return notificationLogsFilterOptionsSelected[fieldName]
          ? notificationLogsFilterOptionsSelected[fieldName]
          : ""
      }
      else {
        return notificationLogsFilterOptionsSelected[fieldName]
          ? notificationLogsFilterOptionsSelected[fieldName]
          : null
      } 
    }
  }

  const isValueSelected = (fieldName: string) => {
    const selectedValue = renderSelectedValue(fieldName);
    
    return selectedValue !== null && selectedValue !== undefined && (Array.isArray(selectedValue) ? selectedValue.length > 0 : true);
  }

  /**
   * This function is used to display filter selected value based on page view 
   * @param fieldName - Name of the field
   */
  const renderFilterOptionsSelected = (fieldName: string) => {
    return renderSelectedValue(fieldName);
  };

  const filterInputStyle = {
    flex: "0 0 calc(20% - 10px)",
    margin: "8px 5px",
    width: "206px",
    "& .MuiOutlinedInput-root": {
      ".input-field": {
        padding: "7.5px 12px"
      },
      padding: 0,
      input: {
        paddingBottom: 0,
      },
    },
  };

  const isStatusFieldDisabled = (fieldName: string) => {
    return fieldName === MULTI_SELECT_FIELD_NAMES.status &&
      ((viewMode === PermissionsFriendlyNames[Permissions.ViewProducts] && productListStatusDisabled) ||
      (viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS] && inactiveItemsStatusDisabled) ||
        (viewMode === PermissionsFriendlyNames[Permissions.ExecutiveDashboard] && dashboardStatusDisabled) ||
        (viewMode === PermissionsFriendlyNames[Permissions.ViewQuotes] && quoteListStatusDisabled) ||
        (viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS] && attachmentListStatusDisabled) ||
        (viewMode === PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT] && productAssignmentListStatusDisabled) ||
        (viewMode === PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard] && projectOverviewDashboardStatusDisabled) ||
        (viewMode === PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS] && notificationLogsStatusDisabled)
      )
  };

  const isFunctionFieldDisabled = (fieldName: string) => {
    return fieldName === MULTI_SELECT_FIELD_NAMES.function && functionDropdownDisabled;
  };

  const renderAutocomplete = (
    fieldName,
    label,
    optionList: Array<any> | undefined,
    optionLabelGetter: (option) => string = null
  ) => {
    const isLoading = isDynamicFilterFieldLoading(fieldName);
    const options = getDynamicFilterOptionList(fieldName, optionList);

    return (
    <Autocomplete
      sx={filterInputStyle}
      className={`filter-autocomplete-dropdown single-input-field ${isValueSelected(fieldName) ? "filter-selected" : ""}`}
      value={
        renderFilterOptionsSelected(fieldName)
      }
      onChange={(event, newValue) => handleFilterChange(fieldName, newValue)}
      onOpen={() => handleDropdownOpen(fieldName)}
      options={options}
      loading={isLoading}
      getOptionLabel={(option) =>
        optionLabelGetter ? optionLabelGetter(option) : option.name || option.description || option
      }
      disableClearable={(fieldName === MULTI_SELECT_FIELD_NAMES.developmentType || fieldName === "assignment")}
      disabled={isStatusFieldDisabled(fieldName) || isFunctionFieldDisabled(fieldName)}
      style={{
        backgroundColor: isStatusFieldDisabled(fieldName) || isFunctionFieldDisabled(fieldName) ? "#EBEBE4" : "#FFFFFF"
      }}
      renderOption={(props, option) => {
          const optionText = optionLabelGetter ? optionLabelGetter(option) : option.name || option.description || option;
          return (
            <li {...props} key={option.id ?? option.name ?? option.description ?? props.id} title={optionText}>
              {optionText}
            </li>
          );
      }}
      renderInput={(params) => (
        <TextField
          key={params.id}
          {...params}
          label={label}
          variant="outlined"
          inputProps={{
            ...params.inputProps,
            title: getInputTitle(params.inputProps?.value),
          }}
        />
      )}
    />
    );
  };

  const renderAutocompleteWithModal = (props: Record<string, any>) => (
    <AutocompleteWithModal
      {...props}
      fieldName={props.fieldName}
      label={props.label}
      optionsList={getDynamicFilterOptionList(props.fieldName, props.optionsList)}
      isOptionsLoading={isDynamicFilterFieldLoading(props.fieldName)}
      onOpenModal={handleDropdownOpen}
      handleCloseModal={props.handleCloseModal}
      handleClear={props.handleClear}
      setIsResetCalled={props.setIsResetCalled}
      isResetCalled={props.isResetCalled}
      preSelectedOptions={props.preSelectedOptions}
      disabled={props.disabled}
      optionLabelGetter={props.optionLabelGetter}
      showSelectAll={props.showSelectAll}
      disabledOptions={props.disabledOptions}
    />
  );

  const renderCheckbox = (
    fieldName,
    label,
    onChange
  ) => (
    <FormControlLabel sx={{marginLeft: "6px"}}
    control={
      <Checkbox
        name={fieldName}
        checked={viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS] ? attachmentListFilterValuesSelected[fieldName]
          : productListFilterValuesSelected[fieldName]}
        onChange={onChange}
      />
    } 
    label={label}              
  />
  );

  const handleResetFilterClick = () => {
    invalidateDynamicOptionsCache();
    restoreInitialFilterOptions();
    if (viewMode === PermissionsFriendlyNames[Permissions.ViewProducts]) {
      dispatch(updateProductListViewSelectedFilterValues({
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0].id],
        hideReleasedToInriver: true,
        hideArchivedDevelopment: true,
      }));
  
      dispatch(updateProductListViewSelectedFilterOptions({
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0]],
        hideReleasedToInriver: true,
        hideArchivedDevelopment: true,
      }));

      setHasShownItemStatusToast(false);
    }
    else if(viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]) {
      dispatch(updateInactiveItemsViewSelectedFilterValues({
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0].id],
        hideReleasedToInriver: true,
        hideArchivedDevelopment: true,
      }));
  
      dispatch(updateInactiveItemsViewSelectedFilterOptions({
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0]],
        hideReleasedToInriver: true,
        hideArchivedDevelopment: true,
      }));
    }
    else if(viewMode === PermissionsFriendlyNames[Permissions.ViewQuotes]) {
      dispatch(updateQuoteListViewSelectedFilterValues(initialSelectedFilters));

      dispatch(updateQuoteListViewSelectedFilterOptions({}));
    }
    else if(viewMode === VIEW_TOOLINGS) {
      dispatch(updateToolingListViewSelectedFilterValues(initialSelectedFilters));

      dispatch(updateToolingListViewSelectedFilterOptions({}));
    }
    else if(viewMode === PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT]) {
      dispatch(updateProductAssignmentsViewSelectedFilterValues(initialSelectedFilters));

      dispatch(updateProductAssignmentsViewSelectedFilterOptions({}));
    }
    else if(viewMode === PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) {
      const findProjectManagerAssignment = filterOptions?.assignments?.find((assignment) => assignment.name === PROJECT_MANAGER_ASSIGNMENT);

      dispatch(updateActiveProjectListSelectedFilterValues({
        assignment: findProjectManagerAssignment?.id,
      }));

      dispatch(updateActiveProjectListSelectedFilterOptions({
        assignment: findProjectManagerAssignment
      }));
    }
    else if (viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS]) {
      dispatch(updateAttachmentViewSelectedFilterValues({
        hideReleasedToInriver: true,
        hideArchivedDevelopment: true,
      }));
  
      dispatch(updateAttachmentViewSelectedFilterOptions({
        hideReleasedToInriver: true,
        hideArchivedDevelopment: true,
      }));
    }
    else if (viewMode === PermissionsFriendlyNames[VIEW_STATUS_REPORTS]) {
      dispatch(updateStatusReportsViewSelectedFilterValues({
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0].id],
      }));
  
      dispatch(updateStatusReportsViewSelectedFilterOptions({
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0]],
      }));
    } else if (viewMode === PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]) {
      dispatch(updateIsUserCleared(false));
      dispatch(updateNotificationLogViewSelectedFilterValues({
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0].id],
        userId: sessionService?.getCurrentUser()?.id || undefined,
      }));
  
      dispatch(updateNotificationLogViewSelectedFilterOptions({
        [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0]],
        userId: {
          id: sessionService?.getCurrentUser()?.id || undefined,
          name: sessionService?.getCurrentUser()?.name || undefined,
        }
      }));

      setHasShownItemStatusToast(false);
    }
    else {
        const setReviewTagFromOptions = filterOptions?.projectTags?.find((pt) => pt.id === dashboardExecutiveReviewTagId);
        dispatch(updateDashboardViewSelectedFilterValues({
          tags: [setReviewTagFromOptions?.id],
        }));
  
        dispatch(updateDashboardViewSelectedFilterOptions({
          tags: [setReviewTagFromOptions],
        }));
    }
    setIsResetCalled(true);
    onApplyFilter({});
    removeAppliedFilterName();
  };

  const showSaveFilterSet = (): boolean => {
    return [PermissionsFriendlyNames[Permissions.ViewProducts], PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS], PermissionsFriendlyNames[Permissions.ViewQuotes], 
      PermissionsFriendlyNames[VIEW_ATTACHMENTS], PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]].includes(viewMode)
  }

  const getPreSelectedFilerOptions = (viewMode, fieldName) => {
    switch(viewMode){
      case PermissionsFriendlyNames[Permissions.ViewProducts]:
        return productListFilterOptionsSelected[fieldName];
      case PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]:
        return inactiveItemsFilterOptionsSelected[fieldName];
      case PermissionsFriendlyNames[Permissions.ViewQuotes]:
        return quoteListFilterOptionsSelected[fieldName];
      case VIEW_TOOLINGS:
        return toolingListFilterOptionsSelected[fieldName];
      case PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT]:
        return productAssignmentsListFilterOptionsSelected[fieldName];
      case PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]:
        return projectOverviewDashboardFilterOptionsSelected[fieldName];
      case PermissionsFriendlyNames[VIEW_STATUS_REPORTS]:
        return statusReportsListFilterOptionsSelected[fieldName];
      case PermissionsFriendlyNames[VIEW_ATTACHMENTS]:
        return attachmentListFilterOptionsSelected[fieldName];
      case PermissionsFriendlyNames[Permissions.ExecutiveDashboard]:
        return dashboardFilterOptionsSelected[fieldName];
      case PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]:
        return notificationLogsFilterOptionsSelected[fieldName];
      default:
        return undefined;
    }
  }

  const getCurrentFilterSets = () => {
    switch(viewMode){
      case PermissionsFriendlyNames[Permissions.ViewProducts]:
        return {key: "products", filterSet: productListFilterOptionsSelected, filterSetValues: productListFilterValuesSelected};
      case PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]:
        return {key: "inactiveProducts", filterSet: inactiveItemsFilterOptionsSelected, filterSetValues: inactiveItemsFilterValuesSelected};
      case PermissionsFriendlyNames[Permissions.ViewQuotes]:
        return {key: "quotes", filterSet: quoteListFilterOptionsSelected, filterSetValues: quoteListFilterValuesSelected};
      case PermissionsFriendlyNames[VIEW_ATTACHMENTS]:
        return {key: "attachments", filterSet: attachmentListFilterOptionsSelected, filterSetValues: attachmentListFilterValuesSelected};
      case PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]:
        return {key: "notificationLogs", filterSet: notificationLogsFilterOptionsSelected, filterSetValues: notificationLogsListFilterValuesSelected};
      default:
        return undefined;
    }
  }

  const getAppliedFilterName = () => {
    const currentFilterSet = getCurrentFilterSets();
    const savedFilterSets = localStorage.getItem("filterSets");
    const parsedFilterSets = savedFilterSets ? JSON.parse(savedFilterSets) : null;

    if(currentFilterSet) {
      return parsedFilterSets ? parsedFilterSets?.appliedFilterSets?.[currentFilterSet.key] : null;
    }
  }

  const removeAppliedFilterName = () => {
    const currentFilterSet = getCurrentFilterSets();
    const savedFilterSets = localStorage.getItem("filterSets");
    const parsedFilterSets = savedFilterSets ? JSON.parse(savedFilterSets) : null;

    if(currentFilterSet) {
      const updatedFilterSets = parsedFilterSets ? { ...parsedFilterSets } : {};
      
      if(updatedFilterSets?.appliedFilterSets?.[currentFilterSet.key]) {
        delete updatedFilterSets.appliedFilterSets[currentFilterSet.key];
        localStorage.setItem("filterSets", JSON.stringify(updatedFilterSets));
      }
    }
  }

  const applyFilterSetInitially = () => {
    const currentFilterSet = getCurrentFilterSets();
    const savedFilterSets = localStorage.getItem("filterSets");
    const parsedFilterSets = savedFilterSets ? JSON.parse(savedFilterSets) : null;

    if(parsedFilterSets?.appliedFilterSets?.[currentFilterSet.key]) {
      const filters = parsedFilterSets?.[currentFilterSet.key]?.find(filter => filter.filterSetName === parsedFilterSets?.appliedFilterSets?.[currentFilterSet.key]);
      const checkboxesFromFilters = {
        hideReleasedToInriver: !!filters?.filterValues?.hideReleasedToInriver,
        hideArchivedDevelopment: !!filters?.filterValues?.hideArchivedDevelopment,
      };
      if (viewMode === PermissionsFriendlyNames[Permissions.ViewProducts]) {
        dispatch(updateProductListViewSelectedFilterValues(filters?.filterValues));
        dispatch(updateProductListViewSelectedFilterOptions(filters?.filterOptions));
        dispatch(updateItemListRequest({...initialItemListRequest, ...filters?.filterValues} as ItemListRequest));
        onApplyFilter({...filters?.filterValues, ...initialItemListRequest, ...checkboxesFromFilters, [MULTI_SELECT_FIELD_NAMES.developmentType]: filters?.filterValues?.developmentType, projectCode: filters?.filterValues?.projectCode || undefined});
      } 
      else if (viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]) {
        dispatch(updateInactiveItemsViewSelectedFilterValues(filters?.filterValues));
        dispatch(updateInactiveItemsViewSelectedFilterOptions(filters?.filterOptions));
        dispatch(updateInactiveItemListRequest({...initialItemListRequest, ...filters?.filterValues} as ItemListRequest));
        onApplyFilter({...filters?.filterValues, ...initialItemListRequest, ...checkboxesFromFilters, [MULTI_SELECT_FIELD_NAMES.developmentType]: filters?.filterValues?.developmentType, projectCode: filters?.filterValues?.projectCode || undefined});
      }
      else if (viewMode === PermissionsFriendlyNames[Permissions.ViewQuotes]) {
        dispatch(updateQuoteListViewSelectedFilterValues(filters?.filterValues));
        dispatch(updateQuoteListViewSelectedFilterOptions(filters?.filterOptions));
        dispatch(updateQuoteListRequest({...initialQuoteListRequest, ...filters?.filterValues} as ItemListRequest));
        onApplyFilter({...filters?.filterValues, ...initialQuoteListRequest, projectCode: filters?.filterValues?.projectCode || undefined});
      } else if (viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS]) {
        dispatch(updateAttachmentViewSelectedFilterValues(filters?.filterValues));
        dispatch(updateAttachmentViewSelectedFilterOptions(filters?.filterOptions));
        dispatch(updateAttachmentListRequest({...initialAttachmentListRequest, ...filters?.filterValues} as ItemListRequest));
        onApplyFilter({...filters?.filterValues, ...initialAttachmentListRequest, ...checkboxesFromFilters, projectCode: filters?.filterValues?.projectCode || undefined});
      } else if (viewMode === PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]) {
        dispatch(updateNotificationLogViewSelectedFilterValues(filters?.filterValues));
        dispatch(updateNotificationLogViewSelectedFilterOptions(filters?.filterOptions));
        dispatch(updateNotificationLogRequest({...initialNotificationLogRequest, ...filters?.filterValues} as ItemListRequest));
        onApplyFilter({...filters?.filterValues, ...initialNotificationLogRequest, [MULTI_SELECT_FIELD_NAMES.developmentType]: filters?.filterValues?.developmentType, projectCode: filters?.filterValues?.projectCode || undefined});
      }
    }
  }

  useEffect(() => {
    const currentFilterSet = getCurrentFilterSets();
    if(currentFilterSet) {
      applyFilterSetInitially();
    }
  }, [])

  const applyFilterAndCloseModal = (filterName: string, filters: SelectedFilters | ItemListRequest, filterOptions: any ) => {
      // update the redux states and apply the filters
      const currentFilterSet = getCurrentFilterSets();
      const savedFilterSets = localStorage.getItem("filterSets");
      const parsedFilterSets = savedFilterSets ? JSON.parse(savedFilterSets) : null;

      if(currentFilterSet) {
        const updatedFilterSets = {
          ...(parsedFilterSets ? parsedFilterSets : {}),
          appliedFilterSets:  {
            ...(parsedFilterSets.appliedFilterSets ? parsedFilterSets.appliedFilterSets : {}),
            [currentFilterSet.key]: filterName
          }
        }
        localStorage.setItem("filterSets", JSON.stringify(updatedFilterSets));
      }
      const checkboxesFromFilters = {
        hideReleasedToInriver: !!filters?.hideReleasedToInriver,
        hideArchivedDevelopment: !!filters?.hideArchivedDevelopment,
      };
      if (viewMode === PermissionsFriendlyNames[Permissions.ViewProducts]) {
        dispatch(updateProductListViewSelectedFilterValues(filters));
        dispatch(updateProductListViewSelectedFilterOptions(filterOptions));
        dispatch(updateItemListRequest({...initialItemListRequest, ...filters} as ItemListRequest));
        onApplyFilter({...filters, ...initialItemListRequest, ...checkboxesFromFilters, [MULTI_SELECT_FIELD_NAMES.developmentType]: filters?.developmentTypes, projectCode: filters.projectCode || undefined});
      } 
      else if (viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS]) {
        dispatch(updateInactiveItemsViewSelectedFilterValues(filters));
        dispatch(updateInactiveItemsViewSelectedFilterOptions(filterOptions));
        dispatch(updateInactiveItemListRequest({...initialItemListRequest, ...filters} as ItemListRequest));
        onApplyFilter({...filters, ...initialItemListRequest, ...checkboxesFromFilters, [MULTI_SELECT_FIELD_NAMES.developmentType]: filters?.developmentTypes, projectCode: filters.projectCode || undefined});
      }
      else if (viewMode === PermissionsFriendlyNames[Permissions.ViewQuotes]) {
        dispatch(updateQuoteListViewSelectedFilterValues(filters));
        dispatch(updateQuoteListViewSelectedFilterOptions(filterOptions));
        dispatch(updateQuoteListRequest({...initialQuoteListRequest, ...filters} as ItemListRequest));
        onApplyFilter({...filters, ...initialQuoteListRequest, projectCode: filters.projectCode || undefined});
      } else if (viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS]) {
        dispatch(updateAttachmentViewSelectedFilterValues(filters));
        dispatch(updateAttachmentViewSelectedFilterOptions(filterOptions));
        dispatch(updateAttachmentListRequest({...initialAttachmentListRequest, ...filters} as ItemListRequest));
        onApplyFilter({...filters, ...initialAttachmentListRequest, ...checkboxesFromFilters, projectCode: filters.projectCode || undefined});
      } else if (viewMode === PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]) {
        dispatch(updateNotificationLogViewSelectedFilterValues(filters));
        dispatch(updateNotificationLogViewSelectedFilterOptions(filterOptions));
        dispatch(updateNotificationLogRequest({...initialNotificationLogRequest, ...filters} as ItemListRequest));
        onApplyFilter({...filters, ...initialNotificationLogRequest, [MULTI_SELECT_FIELD_NAMES.developmentType]: filters?.developmentTypes, projectCode: filters.projectCode || undefined});
      }
    setIsResetCalled(true);
    setFilterSetName("");
    setAnchorEl(null);
  }

  const handleSaveFilterSets = (filterName: string) => {
    const currentFilterSet = getCurrentFilterSets();
    if(currentFilterSet){
      if(filterName?.trim()) {
        if(!Object.keys(currentFilterSet?.filterSetValues)?.length){
          return toast.error("Select at least one value from the filters to save the filter set!");
        }
        const savedFilterSets = localStorage.getItem("filterSets");
        const parsedFilterSets = savedFilterSets ? JSON.parse(savedFilterSets) : null; 
        if(savedFilterSets) {
          const allKeys = parsedFilterSets ? parsedFilterSets[currentFilterSet.key]?.map(filters => filters?.filterSetName) : [];
          if(allKeys?.includes(filterName.trim())) {
            return toast.error("Filter name already exists!");   
          }
        }
        const toBeSavedFilter = {
          filterSetName: filterName?.trim(),
          filterOptions: currentFilterSet?.filterSet,
          filterValues: currentFilterSet?.filterSetValues
        }
        const updatedFilterSet = savedFilterSets ? {
          ...parsedFilterSets,
          [currentFilterSet.key]: parsedFilterSets[currentFilterSet.key] ? [
            ...parsedFilterSets[currentFilterSet.key],
            toBeSavedFilter
            ] : [toBeSavedFilter],
            appliedFilterSets:  {
              ...(parsedFilterSets.appliedFilterSets ? parsedFilterSets.appliedFilterSets : {}),
              [currentFilterSet.key]: filterName
            }
          } : {
            [currentFilterSet.key]: [toBeSavedFilter],
            appliedFilterSets:  {
              [currentFilterSet.key]: filterName
            }
          }
          localStorage.setItem("filterSets", JSON.stringify(updatedFilterSet));
          toast.success(`"${filterName.trim()}" Filter set saved successfully!`);
          onApplyFilter(
            viewMode === PermissionsFriendlyNames[Permissions.ViewProducts] ?
              productListFilterValuesSelected 
              : viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS] ?
                inactiveItemsFilterValuesSelected
              : viewMode === PermissionsFriendlyNames[Permissions.ViewQuotes] ?
                quoteListFilterValuesSelected : viewMode === PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS] ?
                notificationLogsListFilterValuesSelected : attachmentListFilterValuesSelected);  
          setFilterSetName("");
          setAnchorEl(null);
        } else {
          toast.error(!filterName ? "Enter filter name to save the filter set!" : "Incorrect filter name!");
        }
      }
    }

    const getSavedFilterSets = () : {filterOptionsKeys: string, filterSetName: string, filterValues: SelectedFilters, filterOptions: any}[] => {
      const currentFilterSet = getCurrentFilterSets();
      const savedFilterSets = localStorage.getItem("filterSets");

      if(savedFilterSets) {
        const parsedFilterSets = JSON.parse(savedFilterSets);
        if(parsedFilterSets && parsedFilterSets[currentFilterSet.key]) {
          return parsedFilterSets[currentFilterSet.key]?.map(filterSet => {
            const filterName = filterSet.filterSetName;
            const filterOptionsKeys = Object.keys(filterSet.filterOptions)?.map(filterKey => filtersKeyMapping[filterKey]);
            const orderedFilterOptionsKeys = Object.values(filtersKeyMapping).filter(fieldName => filterOptionsKeys?.includes(fieldName));
            return {
              filterSetName: filterName, 
              filterOptionsKeys: orderedFilterOptionsKeys?.join(", "), 
              filterValues: filterSet.filterValues,
              filterOptions: filterSet.filterOptions
            };
          })
        }
      }
    }

    const handleDeleteFilterSet = (filterName: string) => {
      const currentFilterSet = getCurrentFilterSets();
      const savedFilterSets = localStorage.getItem("filterSets");

      if(savedFilterSets) {
        const parsedFilterSets = JSON.parse(savedFilterSets);
        if(parsedFilterSets && parsedFilterSets[currentFilterSet.key]) {
          const updatedFilterSet = {
            ...parsedFilterSets,
            [currentFilterSet.key]:[
              ...parsedFilterSets[currentFilterSet.key],
              ].filter(filterSet => filterSet.filterSetName !== filterName)
            }
            localStorage.setItem("filterSets", JSON.stringify(updatedFilterSet));
            toast.success(`"${filterName.trim()}" Filter set deleted successfully!`);
            setFilterSetName("");
            setDeleteModal({isOpen: false});

            if(parsedFilterSets?.appliedFilterSets?.[currentFilterSet?.key] === filterName){
              removeAppliedFilterName();
            }
        }
      }
    }

  const autoCompleteStatuses = filterOptions.projectStatuses;
  
  const openFilterSets = Boolean(anchorEl);
  const popOverId = openFilterSets ? 'filter-popover' : undefined;
  
  const getItemStatusOptions = () => {
    const optionsArray = filterOptions.itemStatuses?.length
      ? filterOptions.itemStatuses
      : ITEM_STATUS_OPTIONS;
    return optionsArray?.sort(sortByKey()) || [];
  }
  
  return (
    <Card className="common-filters" sx={{ marginBottom: 1 }}>
      <CardActions
        style={{ justifyContent: "space-between", padding: "0 20px" }}
      >
        <span style={{ position: "relative" }}>
          <div style={{ fontSize: "16px", fontWeight: 700, display: "flex"}}>
            <span style={{marginRight: "5px"}}>Filters </span>
            {showSaveFilterSet() && <MoreVert id={popOverId} onClick={(e) => setAnchorEl(e.currentTarget as unknown as HTMLElement)} />}
            {getAppliedFilterName() && <span>{getAppliedFilterName()}</span>}
          </div>
          {showSaveFilterSet() && <Popover
            id={popOverId}
            open={openFilterSets}
            onClose={() => setAnchorEl(null)}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            className="filter-sets-modal"
          >
            <div className="filter-set-container">
              <div className="filter-set-save-input">
                <TextField
                  sx={{...filterInputStyle, flex: "none", width: "70%"}}
                  className="hide-input-arrow"
                  InputProps={{
                    inputProps: {
                        className: "input-field"
                    }
                  }}
                  placeholder="Enter your text here"
                  variant="outlined"
                  inputProps={{ type: "number" }}
                  onKeyDown={(e) => (e.key === "ArrowUp" || e.key === "ArrowDown") && e.preventDefault()}
                  onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
                  onChange={(event) => setFilterSetName(event.target.value)}
                />
                <Button
                  className="save-button"
                  onClick={() => handleSaveFilterSets(filterSetName)}
                >
                  SAVE
                </Button> 
              </div>
              {localStorage.getItem("filterSets") && JSON.parse(localStorage.getItem("filterSets"))[getCurrentFilterSets().key]?.length > 0 && <hr/>}
              <div className="filter-sets-list">
                {localStorage.getItem("filterSets") && getSavedFilterSets()?.reverse()?.map(filterSet => <div
                  className="saved-filter-set"
                  key={filterSet.filterSetName}
                >
                  <div className="filter-set" onClick={() => applyFilterAndCloseModal(filterSet.filterSetName, filterSet.filterValues, filterSet.filterOptions)}>
                    <span className="filter-set-name">{filterSet.filterSetName}</span>
                    <span className="filter-set-options" title={filterSet.filterOptionsKeys}>{filterSet.filterOptionsKeys}</span>
                  </div>
                  <div className="remove-button"><Close onClick={() => setDeleteModal({isOpen: true, filterName:filterSet.filterSetName})} /></div>
                </div>)}
              </div>
            </div>
            {deleteModal?.filterName && <Dialog
              open={deleteModal.isOpen}
              onClose={() => setDeleteModal({isOpen: false })}
              aria-labelledby="deleteModal"
              className="delete-filter-set-confirmation-modal"
            >
              <DialogTitle id="deleteModal">
                Delete Filter set Confirmation
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {`Do you want to delete the filter set "${deleteModal?.filterName}"?`}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button autoFocus onClick={() => setDeleteModal({isOpen: false })}>
                  Cancel
                </Button>
                <Button className="delete-button" onClick={() => handleDeleteFilterSet(deleteModal?.filterName)}>Delete</Button>
              </DialogActions>
            </Dialog>}
          </Popover>}
        </span>
        <Button
          onClick={handleExpandClick}
          endIcon={filtersExpanded ? <ExpandLess /> : <ExpandMore />}
        >
          {filtersExpanded ? "Collapse" : "Expand"}
        </Button>
      </CardActions>
      <CardContent style={{ paddingBottom: filtersExpanded ? 2 : 0 }}>
        <Collapse in={filtersExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ display: "flex", flexWrap: "wrap" }}>
            {renderAutocomplete("brandCollection", "Brand Collection", filterOptions.brands)}
            {![VIEW_TOOLINGS, VIEW_STATUS_REPORTS].includes(viewMode) && 
            renderAutocompleteWithModal({
              fieldName: MULTI_SELECT_FIELD_NAMES.stage, 
              label: "Stages", 
              optionsList: filterOptions.projectStages,
              handleCloseModal: (fieldName, newValue) => handleFilterChange(fieldName, newValue),
              handleClear: (fieldName) => handleFilterChange(fieldName, null),
              preSelectedOptions: getPreSelectedFilerOptions(viewMode, MULTI_SELECT_FIELD_NAMES.stage),
              setIsResetCalled: setIsResetCalled,
              isResetCalled: isResetCalled,
            })}
            {![VIEW_TOOLINGS, VIEW_STATUS_REPORTS].includes(viewMode) && 
            renderAutocompleteWithModal({
              fieldName: MULTI_SELECT_FIELD_NAMES.status,
              label: "Statuses",
              optionsList: autoCompleteStatuses,
              handleCloseModal: (fieldName, newValue) => handleFilterChange(fieldName, newValue),
              handleClear: (fieldName) => handleFilterChange(fieldName, null),
              disabled: isStatusFieldDisabled(MULTI_SELECT_FIELD_NAMES.status),
              preSelectedOptions: getPreSelectedFilerOptions(viewMode, MULTI_SELECT_FIELD_NAMES.status),
              setIsResetCalled: setIsResetCalled,
              isResetCalled: isResetCalled,
            })}
            {renderAutocompleteWithModal({
              fieldName: MULTI_SELECT_FIELD_NAMES.marketingDesigner,
              label: "Designers",
              optionsList: filterOptions.productDesignerMkts,
              handleCloseModal: (fieldName, newValue) => handleFilterChange(fieldName, newValue),
              handleClear: (fieldName) => handleFilterChange(fieldName, null),
              preSelectedOptions: getPreSelectedFilerOptions(viewMode, MULTI_SELECT_FIELD_NAMES.marketingDesigner),
              setIsResetCalled: setIsResetCalled,
              isResetCalled: isResetCalled,
            })}
            {(viewMode !== PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) &&
              renderAutocompleteWithModal({
                fieldName: MULTI_SELECT_FIELD_NAMES.seriesName,
                label: "Series Names",
                optionsList: filterOptions.seriesNames,
                handleCloseModal: (fieldName, newValue) => handleFilterChange(fieldName, newValue),
              handleClear: (fieldName) => handleFilterChange(fieldName, null),
              preSelectedOptions: getPreSelectedFilerOptions(viewMode, MULTI_SELECT_FIELD_NAMES.seriesName),
              setIsResetCalled: setIsResetCalled,
              isResetCalled: isResetCalled,
              })}
            {(![VIEW_TOOLINGS, PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard], VIEW_PRODUCT_ASSIGNMENT].includes(viewMode)) &&
              renderAutocompleteWithModal({
                fieldName: MULTI_SELECT_FIELD_NAMES.pdFamily,
                label: "PD Families",
                optionsList: filterOptions.pdFamilyNames,
                handleCloseModal: (fieldName, newValue) => handleFilterChange(fieldName, newValue),
                handleClear: (fieldName) => handleFilterChange(fieldName, null),
                preSelectedOptions: getPreSelectedFilerOptions(viewMode, MULTI_SELECT_FIELD_NAMES.pdFamily),
                setIsResetCalled: setIsResetCalled,
                isResetCalled: isResetCalled,
              })}
            {(viewMode !== PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) &&
            (viewMode !== VIEW_TOOLINGS) &&
              renderAutocomplete(
                "category",
                "Category",
                filterOptions.productcategories
              )}
            {(viewMode !== PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) &&
            (viewMode !== VIEW_TOOLINGS) &&
            renderAutocompleteWithModal({
                fieldName: MULTI_SELECT_FIELD_NAMES.function,
                label: "Functions",
                optionsList: filterOptions.productFunctions,
                handleCloseModal: (fieldName, newValue) => handleFilterChange(fieldName, newValue),
                handleClear: (fieldName) => handleFilterChange(fieldName, null),
                disabled: isFunctionFieldDisabled(MULTI_SELECT_FIELD_NAMES.function),
                preSelectedOptions: getPreSelectedFilerOptions(viewMode, MULTI_SELECT_FIELD_NAMES.function),
                setIsResetCalled: setIsResetCalled,
                isResetCalled: isResetCalled,
              })}
            {(viewMode !== PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) &&
            (viewMode !== VIEW_TOOLINGS) &&
              renderAutocomplete(
                "baseCode",
                "Base Code",
                filterOptions.baseCodes
              )}
            {(viewMode !== PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) &&
            ![VIEW_TOOLINGS, VIEW_PRODUCT_ASSIGNMENT].includes(viewMode) &&
              renderAutocomplete("pdCode", "PD BaseCode", filterOptions.pdCodes)}
            {(viewMode !== PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) &&
            ![VIEW_TOOLINGS, VIEW_PRODUCT_ASSIGNMENT].includes(viewMode) &&
              renderAutocomplete(
                "stockCode",
                "Stock Code",
                filterOptions.stockCodes
              )}
            {(viewMode !== PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) &&
            ![VIEW_TOOLINGS, VIEW_PRODUCT_ASSIGNMENT].includes(viewMode) &&
              renderAutocomplete(
                "primaryMaterial",
                "Primary Material",
                filterOptions.primaryMaterials
              )}
            {(viewMode !== PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) &&
            ![VIEW_TOOLINGS, VIEW_PRODUCT_ASSIGNMENT].includes(viewMode) &&
              renderAutocomplete(
                "secondaryMaterial",
                "Secondary Material",
                filterOptions.secondaryMaterials
              )}
            {(viewMode !== PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) &&
            ![VIEW_TOOLINGS].includes(viewMode) &&
            renderAutocompleteWithModal({
                fieldName: MULTI_SELECT_FIELD_NAMES.slIntroDate,
                label: "SL Intro Dates",
                optionsList: filterOptions.productIntroDatess,
                optionLabelGetter: formatSlIntroDateFilterOption,
                handleCloseModal: (fieldName, newValue) => handleFilterChange(fieldName, newValue),
                handleClear: (fieldName) => handleFilterChange(fieldName, null),
                preSelectedOptions: getPreSelectedFilerOptions(viewMode, MULTI_SELECT_FIELD_NAMES.slIntroDate),
                setIsResetCalled: setIsResetCalled,
                isResetCalled: isResetCalled,
              })}
            {(viewMode !== PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) &&
            ![VIEW_TOOLINGS, VIEW_PRODUCT_ASSIGNMENT].includes(viewMode) &&
              renderAutocompleteWithModal({
                fieldName: MULTI_SELECT_FIELD_NAMES.tags,
                label: "Tags",
                optionsList: filterOptions.projectTags,
                handleCloseModal: (fieldName, newValue, reason) => handleTagsFilterChange(fieldName, newValue, reason),
                handleClear: (fieldName) => handleFilterChange(fieldName, viewMode === PermissionsFriendlyNames[Permissions.ExecutiveDashboard] ? filterOptions?.projectTags?.filter((tag) => tag.id === dashboardExecutiveReviewTagId) : null),
                preSelectedOptions: getPreSelectedFilerOptions(viewMode, MULTI_SELECT_FIELD_NAMES.tags),
                setIsResetCalled: setIsResetCalled,
                isResetCalled: isResetCalled,
                showSelectAll: true,
                disabledOptions: viewMode === PermissionsFriendlyNames[Permissions.ExecutiveDashboard] ? filterOptions?.projectTags?.filter((tag) => tag.id === dashboardExecutiveReviewTagId).map(item => item.id) : [],
              })}
            {(viewMode !== PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) &&
            ![VIEW_PRODUCT_ASSIGNMENT].includes(viewMode) &&
            renderAutocompleteWithModal({
                fieldName: MULTI_SELECT_FIELD_NAMES.vendor,
                label: "Vendors",
                optionsList: filterOptions?.productSuppliers,
                handleCloseModal: (fieldName, newValue) => handleFilterChange(fieldName, newValue),
                handleClear: (fieldName) => handleFilterChange(fieldName, null),
                preSelectedOptions: getPreSelectedFilerOptions(viewMode, MULTI_SELECT_FIELD_NAMES.vendor),
                setIsResetCalled: setIsResetCalled,
                isResetCalled: isResetCalled,
              })}
            {([PermissionsFriendlyNames[Permissions.ViewProducts], PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS], PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS], PermissionsFriendlyNames[VIEW_STATUS_REPORTS]].includes(viewMode)) &&
              renderAutocompleteWithModal({
                fieldName: MULTI_SELECT_FIELD_NAMES.developmentType,
                label: "Development Types",
                optionsList: PRODUCT_TYPE_DROPDOWN_OPTIONS,
                handleCloseModal: (fieldName, newValue) => handleFilterChange(fieldName, newValue),
                handleClear: (fieldName) => handleFilterChange(fieldName, null),
                preSelectedOptions: getPreSelectedFilerOptions(viewMode, MULTI_SELECT_FIELD_NAMES.developmentType),
                setIsResetCalled: setIsResetCalled,
                isResetCalled: isResetCalled,
              })}
            {(viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS]) &&
              renderAutocomplete(
                "attachmentTypeId",
                "Attachment Type",
                filterOptions.attachmentTypes
              )}
            {(viewMode === PermissionsFriendlyNames[Permissions.ViewQuotes]) &&
            ![VIEW_TOOLINGS, VIEW_PRODUCT_ASSIGNMENT].includes(viewMode) &&
              renderAutocomplete(
                "quoteDate",
                "Quote Date",
                filterOptions.quoteDates,
                (option) => moment(option).format(DATE_FORMAT)
              )}
            {(viewMode === PermissionsFriendlyNames[Permissions.ViewQuotes]) &&
            ![VIEW_TOOLINGS, VIEW_PRODUCT_ASSIGNMENT].includes(viewMode) &&
              renderAutocomplete(
                "quoteApprovalType",
                "Quote Approval Type",
                filterOptions.quoteApprovalTypes?.length
                  ? filterOptions.quoteApprovalTypes
                  : QUOTE_APPROVAL_TYPE_DROPDOWN_OPTIONS
              )}
            {([PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard], VIEW_PRODUCT_ASSIGNMENT].includes(viewMode)) &&
              renderAutocomplete(
                viewMode === PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT] ? "assignmentId" : "assignment",
                "Assignment",
                filterOptions.assignments
              )}
            {[PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT], PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]].includes(viewMode) &&
              renderAutocomplete(
                "userId",
                "User",
                filterOptions.users
              )}
            {(viewMode !== PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard]) &&
            ![VIEW_TOOLINGS, VIEW_PRODUCT_ASSIGNMENT].includes(viewMode) &&
              <TextField
                sx={filterInputStyle}
                className={`hide-input-arrow search-product-id ${renderFilterOptionsSelected("projectCode") ? "filter-selected" : ""}`}
                InputProps={{
                  inputProps: {
                      className: "input-field"
                  }
                }}
                label="Search Product Id"
                variant="outlined"
                inputProps={{ type: "number" }}
                value={
                  renderFilterOptionsSelected("projectCode")
                }
                onKeyDown={(e) => (e.key === "ArrowUp" || e.key === "ArrowDown") && e.preventDefault()}
                onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
                onChange={(event) =>
                  handleFilterChange("projectCode", (event.target.value).trim())
                }
              />}
              {([PermissionsFriendlyNames[Permissions.ViewProducts], PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS], PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS]].includes(viewMode)) &&
              renderAutocomplete(
                "itemStatus",
                "Item Status",
                getItemStatusOptions(),
              )}
            {viewMode !== PermissionsFriendlyNames[VIEW_STATUS_REPORTS] &&
              <Button
                variant="contained"
                style={{ margin: "8px 5px", width: "135px", maxHeight: "37px" }}
                onClick={() => onApplyFilter(
                  viewMode === PermissionsFriendlyNames[Permissions.ViewProducts] ?
                    productListFilterValuesSelected 
                    : viewMode === PermissionsFriendlyNames[VIEW_INACTIVE_PRODUCTS] ?
                      inactiveItemsFilterValuesSelected
                    : viewMode === PermissionsFriendlyNames[Permissions.ViewQuotes] ?
                      quoteListFilterValuesSelected : viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS] ?
                        attachmentListFilterValuesSelected : viewMode === VIEW_TOOLINGS ?
                          toolingListFilterValuesSelected : viewMode === PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT] ?
                          productAssignmentsListFilterValuesSelected : viewMode === PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard] ?
                            projectOverviewDashboardFilterValuesSelected :
                            viewMode === PermissionsFriendlyNames[VIEW_NOTIFICATION_LOGS] ?
                              notificationLogsListFilterValuesSelected : dashboardFilterValuesSelected)
                }
              >
                Search
              </Button>
            }
            <Button
              variant={viewMode !== PermissionsFriendlyNames[VIEW_STATUS_REPORTS] ? "outlined" : "contained"}
              style={{ margin: "8px 5px", width: "135px", maxHeight: "37px" }}
              onClick={handleResetFilterClick}
            >
              Reset
            </Button>            
            <div className={`filter-checkboxes ${viewMode === PermissionsFriendlyNames[Permissions.ViewProducts] ? "product-listing-page" : ""}`}>
              {((viewMode === PermissionsFriendlyNames[Permissions.ViewProducts]) || 
                (viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS])) &&
                renderCheckbox(
                  "hideReleasedToInriver",
                  "Hide Released To Inriver",
                  (e) => HandleCheckboxChange("hideReleasedToInriver",e.target.checked)
                )
              }
              {((viewMode === PermissionsFriendlyNames[Permissions.ViewProducts]) || 
                (viewMode === PermissionsFriendlyNames[VIEW_ATTACHMENTS])) &&
                renderCheckbox(
                  "hideArchivedDevelopment",
                  "Hide Archived Development",
                  (e) => HandleCheckboxChange("hideArchivedDevelopment",e.target.checked)
                )
              }
            </div>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};
export default Filters;
